import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getBulletins, getBulletin, createBulletin, deleteBulletin, genererBulletinsMasse,
  getFactures, getFacture, createFacture, updateFacture, deleteFacture,
  getEmployes, getEmploye, getProjets
} from '../services/api';
import BulletinRepartitionProjet from '../components/BulletinRepartitionProjet';
import { useExportExcel, fmtDate } from '../hooks/useExportExcel';

// ─── Constants & Helpers ─────────────────────────────────────────────────────
const MOIS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];
const now = new Date();
const fmt = n => new Intl.NumberFormat('fr-CI').format(Math.round(n || 0));
const dateEdition = () => new Date().toLocaleDateString('fr-FR');

/** Informations d'organisation (en-tête du bulletin) — communes aux deux
 *  formats (ancien et nouveau). Extraites de BULLETIN REVISION.xlsx. */
const ORG_INFO = {
  adresse:      '27 BP 283 Abidjan 27',
  siege:        'Abidjan Angré 7ème tranche',
  cnps:         '159633',
  contribuable: '0913190U',
  email:        'gfm3@yahoo.fr',
  telephone:    '27 22 52 20 65',
};

/** Calcule l'ancienneté en années entre date_debut et la fin du mois de paie.
 *  §3.8 — Formule officielle KKA : DAYS(date_edition, date_debut) / 365.25
 *  (date_edition ≈ dernier jour du mois de paie). */
function calcAnciennete(dateDebut, moisPaie, anneePaie) {
  if (!dateDebut) return 0;
  const debut = new Date(dateDebut);
  if (isNaN(debut)) return 0;
  // Date de référence = dernier jour du mois de paie (≈ date d'édition)
  const ref = new Date(anneePaie, moisPaie, 0); // moisPaie est 1-based → mois JS = moisPaie, jour 0 = dernier du mois précédent
  if (ref <= debut) return 0;
  const jours = Math.round((ref - debut) / 86400000);
  return Math.max(0, parseFloat((jours / 365.25).toFixed(3)));
}

/** §3.22 — Formate une date ISO (YYYY-MM-DD, telle que renvoyée par le
 *  backend) au format Jour/Mois/Année (ex: "04/01/2024"), demandé par les
 *  RH pour l'affichage des dates (Date d'embauche, Date de fin de contrat)
 *  sur le bulletin, aux deux formats (ancien et nouveau). Retourne '—' si
 *  la date est absente/invalide ou déjà un texte non-date (ex: "CDI"). */
function fmtDateFR(dateStr) {
  if (!dateStr) return '—';
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr; // déjà un texte (ex: "CDI") ou format inattendu
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Formate une ancienneté en années décimales (ex: 4.083) au format
 *  "X (ans) Y mois", conforme à l'affichage du fichier BULLETIN REVISION.xlsx. */
function fmtAnciennete(anneesDecimales) {
  const annees = parseFloat(anneesDecimales) || 0;
  const totalMois = Math.round(annees * 12);
  const ans = Math.floor(totalMois / 12);
  const mois = totalMois % 12;
  return `${ans} (ans) ${mois} mois`;
}

/** §3.8 — Paramètres de paie alignés sur BULLETIN REVISION.xlsx et sur
 *  PaieController::loadPaieParams() (mêmes valeurs par défaut). Ce bloc
 *  duplique côté frontend les taux/plafonds utilisés par le backend afin
 *  que l'aperçu "en direct" du formulaire corresponde au bulletin qui sera
 *  réellement enregistré. Si un administrateur modifie ces taux depuis
 *  Paramètres > section "paie" (table `parametres`), CETTE CONSTANTE doit
 *  être mise à jour en conséquence (le calcul définitif et persisté reste
 *  de toute façon celui du backend, qui lit `parametres` dynamiquement). */
const PAIE_PARAMS = {
  plafondCnpsRetraite:            1_647_315,
  plafondCnpsPfAt:                75_000,  // Aligné sur BULLETIN REVISION.xlsx (§3.12)
  tauxCnpsRetraiteSalarie:        0.063,   // 6,3 %
  tauxCnpsRetraitePatronal:       0.077,   // 7,7 %
  tauxCnpsPrestationsFamiliales:  0.0575,  // 5,75 %
  tauxCnpsAccidentTravail:        0.02,    // 2 %
  tauxFdfpApprentissage:          0.004,   // 0,4 %
  tauxFdfpFormation:              0.006,   // 0,6 %
  tauxImpotSalaires:              0.012,   // IS 1,2 % (ex "ITS employeur" du fichier Excel)
  cmuMontantPatronal:             500,     // par enfant à charge (§3.11)
  cmuMontantSalarial:             500,     // par enfant à charge (§3.11)
  tauxPrimeAncienneteParAn:       2,       // 2 %/an (point de départ après le seuil de 2 ans)
  plafondPrimeAncienneteParPct:   25,      // plafonné à 25 %
};

/** §3.8 — Nombre de parts fiscales : table de correspondance Situation
 *  matrimoniale × Nombre d'enfants, extraite et vérifiée depuis le fichier
 *  "DE PAIE EXO KKA 30012025 Calcul anciennete.xlsx" — IDENTIQUE à
 *  PaieController::calcNombreParts(). */
function calcNombreParts(situationMatrimoniale, nbEnfants) {
  const sit = (situationMatrimoniale || '').toLowerCase().trim();
  const n = Math.max(0, parseInt(nbEnfants) || 0);
  const tableMarie          = { 0: 2, 1: 2.5, 2: 3, 3: 3.5, 4: 4, 5: 4.5, 6: 5 };
  const tableCelibDivorce   = { 0: 1, 1: 2,   2: 2.5, 3: 3, 4: 3.5, 5: 4, 6: 4.5, 7: 5 };
  const tableVeuf           = { 0: 1, 1: 2.5, 2: 3, 3: 3.5, 4: 4, 5: 4.5, 6: 5 };
  if (sit.includes('marié') || sit.includes('marie')) return tableMarie[n] ?? 5;
  if (sit.includes('veu')) return tableVeuf[n] ?? 5;
  if (sit.includes('célibataire') || sit.includes('celibataire') || sit.includes('divorc')) return tableCelibDivorce[n] ?? 5;
  return 5;
}

/** §3.8 — ICF : barème progressif MENSUEL par tranches sur le brut imposable
 *  prorata (X). Remplace l'ancien barème ITS annualisé. IDENTIQUE à
 *  PaieController::calcICF() — extrait et vérifié depuis le fichier KKA.
 *  §3.14 — tranche TR6 (au-delà de 8 000 000 FCFA, 32 %) ajoutée : elle
 *  manquait (le taux restait plafonné à 28 % indéfiniment au-delà de 8M),
 *  conformément au support de formation officiel "DEDUCTION & IMPOT sur
 *  salaire.pptx" (barème complet à 6 tranches, réforme du 13/09/2023). */
function calcICF(brut) {
  const x = brut;
  const tr1 = 0;
  const tr2 = x <= 75_000 ? 0 : x < 240_000 ? (x - 75_000) * 0.16 : 165_000 * 0.16;
  const tr3 = x <= 240_000 ? 0 : x < 800_000 ? (x - 240_000) * 0.21 : 560_000 * 0.21;
  const tr4 = x <= 800_000 ? 0 : x < 2_400_000 ? (x - 800_000) * 0.24 : 1_600_000 * 0.24;
  const tr5 = x <= 2_400_000 ? 0 : x < 8_000_000 ? (x - 2_400_000) * 0.28 : 5_600_000 * 0.28;
  const tr6 = x <= 8_000_000 ? 0 : (x - 8_000_000) * 0.32; // §3.14 — tranche manquante ajoutée
  return tr1 + tr2 + tr3 + tr4 + tr5 + tr6;
}

/** §3.8 — RICF = (nombre de parts − 1) × 11 000 FCFA. IDENTIQUE à
 *  PaieController::calcRICF(). */
function calcRICF(parts) {
  return Math.max(0, (parts - 1) * 11_000);
}

/** Toutes les charges depuis un objet form (utilisé pour preview live).
 *  §3.9 — Reproduit exactement PaieController::bulletinCreate() : SEUL le
 *  brut total (base de calcul de toutes les charges CNPS/ITS/FDFP...) est
 *  proratisé sur jours_travailles/30. Les lignes de détail affichées sur
 *  le bulletin (Salaire catégoriel, Sursalaire, Prime d'ancienneté)
 *  restent affichées à leur montant nominal, sur demande explicite des RH
 *  (transport, logement, indemnité de représentation NON imposables,
 *  ajoutés après). §3.19 — Net à payer = brut imposable − charges
 *  SALARIALES uniquement (charges patronales non déduites), sur demande
 *  explicite des RH — remplace la formule précédente (§3.1/§3.14) qui
 *  déduisait à la fois les charges patronales et salariales.
 *  Ancienneté / nombre de parts / "nouvelle imposition" (ICF/RICF) selon
 *  les formules officielles KKA, vérifiées §3.14 (tranche ICF >8M ajoutée).
 *  §3.25 — CMU (salariale ET patronale) = montant unitaire × cmuPersonnes
 *  (employé(e) + conjoint(e) si marié(e) + enfants à charge), sur demande
 *  explicite des RH — remplace la formule §3.14 (nombre d'enfants + 1). */
function computeCharges(form) {
  const base      = parseFloat(form.salaire_base) || 0;
  const sursal    = parseFloat(form.sursalaire)   || 0;
  const rep       = parseFloat(form.indemnite_rep)|| 0;
  const transp    = parseFloat(form.transport)    || 0;
  const loge      = parseFloat(form.logement)     || 0;
  const tropPercu = parseFloat(form.trop_percu)   || 0;
  const joursTravailles = parseFloat(form.jours_travailles) || 30;

  // Nombre de parts fiscales (§3.8 — fichier KKA)
  const situationMatrimoniale = form.matrimonial || form.situation_matrimoniale || 'Célibataire';
  const nbEnfants  = parseInt(form.nb_enfants) || 0;
  const parts      = (form.parts_familiales && parseFloat(form.parts_familiales) > 0)
                        ? parseFloat(form.parts_familiales)
                        : calcNombreParts(situationMatrimoniale, nbEnfants);

  // §3.25 — Nombre de personnes couvertes par la CMU = l'employé(e) (+1)
  // + son/sa conjoint(e) (+1 SI marié(e)) + chaque enfant à charge, sur
  // demande explicite des RH (ex : marié(e), 1 enfant → employé + conjoint
  // + enfant = 3 pers. × 1 000 FCFA = 3 000 FCFA total ; remplace la
  // formule §3.14 qui ne comptait que l'employé(e) + enfants, sans le
  // conjoint pour les personnes mariées).
  const estMarie = /mari/i.test(situationMatrimoniale);
  const cmuPersonnes = nbEnfants + 1 + (estMarie ? 1 : 0);

  // Prime d'ancienneté (§3.8 — fichier KKA) : 0 % avant 24,995 mois, puis
  // 2 % + 1 %/an entamée au-delà, plafonnée à 25 %.
  const ancienneteAnnees = parseFloat(form.anciennete) || 0;
  const ancienneteMois    = ancienneteAnnees * 12;
  const ancPct = ancienneteMois <= 24.995
    ? 0
    : Math.min(
        (PAIE_PARAMS.tauxPrimeAncienneteParAn + Math.max(0, Math.floor((ancienneteMois - 24) / 12)) * 1) / 100,
        PAIE_PARAMS.plafondPrimeAncienneteParPct / 100
      );
  const ancMnt = Math.round(base * ancPct);

  // §3.9 — Brut imposable, proratisé sur les jours travaillés (/30) : SEUL
  // le brut total est proratisé (base de calcul de toutes les charges) —
  // les lignes de détail (Salaire catégoriel, Sursalaire, Prime
  // d'ancienneté) affichées sur le bulletin restent à leur montant nominal,
  // sur demande explicite des RH. Aligné sur PaieController::bulletinCreate().
  const brutImposableBase = base + sursal + ancMnt;
  const brutImpos = Math.round(brutImposableBase * joursTravailles / 30);

  // Retenues salariales — ITS (nouvelle imposition, §3.8) = MAX(0, ICF − RICF)
  const cnpsSal = Math.round(Math.min(brutImpos, PAIE_PARAMS.plafondCnpsRetraite) * PAIE_PARAMS.tauxCnpsRetraiteSalarie);
  // §3.25 — CMU salariale = montant unitaire × cmuPersonnes (employé(e) +
  // conjoint(e) si marié(e) + enfants à charge), cf. calcul ci-dessus.
  const cmuSal  = PAIE_PARAMS.cmuMontantSalarial * cmuPersonnes;
  const icf     = calcICF(brutImpos);
  const ricf    = calcRICF(parts);
  const its     = Math.max(0, Math.round(icf - ricf));
  const totRet  = cnpsSal + cmuSal + its;

  // Brut total (imposable + exonérés) — affichage uniquement
  const brutTotal = brutImpos + rep + transp + loge;

  // Charges patronales
  const plafondPfAt      = PAIE_PARAMS.plafondCnpsPfAt;
  const cnpsPatRetraite   = Math.round(Math.min(brutImpos, PAIE_PARAMS.plafondCnpsRetraite) * PAIE_PARAMS.tauxCnpsRetraitePatronal);
  const cnpsPatPF         = Math.round(Math.min(brutImpos, plafondPfAt) * PAIE_PARAMS.tauxCnpsPrestationsFamiliales);
  const cnpsPatAT         = Math.round(Math.min(brutImpos, plafondPfAt) * PAIE_PARAMS.tauxCnpsAccidentTravail);
  const fdfpApprent       = Math.round(brutImpos * PAIE_PARAMS.tauxFdfpApprentissage);
  const fdfpFormation     = Math.round(brutImpos * PAIE_PARAMS.tauxFdfpFormation);
  // §3.25 — CMU patronale = montant unitaire × cmuPersonnes, même règle
  // que la CMU salariale (voir plus haut).
  const cmuPat            = PAIE_PARAMS.cmuMontantPatronal * cmuPersonnes;
  const impotSalaires     = Math.round(brutImpos * PAIE_PARAMS.tauxImpotSalaires);
  const totPat = cnpsPatRetraite + cnpsPatPF + cnpsPatAT + fdfpApprent + fdfpFormation + cmuPat + impotSalaires;

  // §3.19 — Net à payer : le brut imposable ne déduit plus que les charges
  // SALARIALES (plus les charges patronales), sur demande explicite des RH.
  // Remplace la formule précédente (§3.1/§3.14) qui déduisait les deux.
  const net = (brutImpos - totRet) + rep + transp + loge - tropPercu;

  return {
    base, sursal, rep, transp, loge, tropPercu, joursTravailles, nbEnfants,
    parts, ancPct, ancMnt, brutImpos, brutTotal, cmuPersonnes,
    cnpsSal, cmuSal, icf, ricf, its, totRet, net,
    cnpsPatRetraite, cnpsPatPF, cnpsPatAT, fdfpApprent, fdfpFormation, cmuPat, impotSalaires, totPat,
  };
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

.pr  { font-family:'DM Sans',Arial,sans-serif; font-size:14px; color:#1a1a2e; }
.pr * { box-sizing:border-box; }

/* Toast */
.pr-toast {
  position:fixed; bottom:22px; right:22px; z-index:9999;
  padding:11px 20px; border-radius:8px; font-size:13px; font-weight:600;
  color:#fff; box-shadow:0 4px 20px rgba(0,0,0,.22); animation:prSlide .2s ease;
}
.pr-toast.success{background:#1b5e20;} .pr-toast.error{background:#b71c1c;}
@keyframes prSlide{from{transform:translateY(14px);opacity:0}to{transform:translateY(0);opacity:1}}

/* Page header */
.pr-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:16px;}
.pr-header h2{font-size:20px;font-weight:700;color:#0f2644;margin:0 0 3px;}
.pr-header p{font-size:12px;color:#6b7a93;margin:0;}
.pr-header-right{display:flex;align-items:center;gap:8px;flex-shrink:0;margin-left:16px;}

/* Selects */
.pr-sel{
  font-size:13px;padding:6px 10px;border:1.5px solid #c8d4e0;
  border-radius:6px;background:#fff;color:#0f2644;outline:none;
  font-family:'DM Sans',sans-serif;cursor:pointer;transition:border-color .15s;
}
.pr-sel:focus{border-color:#1a5eb8;}

/* Buttons */
.pr-btn{
  display:inline-flex;align-items:center;gap:6px;
  padding:7px 14px;border-radius:7px;border:none;cursor:pointer;
  font-size:13px;font-weight:600;font-family:'DM Sans',sans-serif;
  transition:opacity .15s;white-space:nowrap;
}
.pr-btn:hover{opacity:.87;} .pr-btn:disabled{opacity:.5;cursor:not-allowed;}
.pr-btn.green{background:#16a34a;color:#fff;}
.pr-btn.blue {background:#1a5eb8;color:#fff;}
.pr-btn.dark {background:#0f2644;color:#fff;}
.pr-btn.gray {background:#e8edf3;color:#333;}
.pr-btn.sm   {padding:5px 11px;font-size:12px;}

/* KPI cards */
.pr-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px;}
.pr-kpi{
  background:#fff;border:1px solid #e2e8f0;border-radius:10px;
  padding:16px 18px;box-shadow:0 1px 4px rgba(0,0,0,.05);
}
.pr-kpi-label{
  font-size:10.5px;color:#6b7a93;text-transform:uppercase;
  letter-spacing:.07em;font-weight:600;margin-bottom:8px;
}
.pr-kpi-val{font-size:24px;font-weight:700;line-height:1.1;}
.pr-kpi-val.blue  {color:#1a5eb8;} .pr-kpi-val.green {color:#166534;}
.pr-kpi-val.orange{color:#c2410c;} .pr-kpi-val.red   {color:#991b1b;}

/* Single-level tabs */
.pr-tabs{display:flex;border-bottom:2px solid #e2e8f0;margin-bottom:18px;}
.pr-tab{
  padding:9px 18px;cursor:pointer;font-size:13px;font-weight:600;
  color:#6b7a93;border:none;background:none;outline:none;
  border-bottom:3px solid transparent;margin-bottom:-2px;
  transition:color .15s,border-color .15s;
}
.pr-tab:hover{color:#0f2644;} .pr-tab.active{color:#0f2644;border-bottom-color:#16a34a;}

/* Card */
.pr-card{
  background:#fff;border:1px solid #e2e8f0;border-radius:10px;
  padding:18px;margin-bottom:14px;box-shadow:0 1px 4px rgba(0,0,0,.05);
}
.pr-card-title{
  font-size:13px;font-weight:700;color:#0f2644;
  margin-bottom:14px;padding-bottom:10px;border-bottom:2px solid #f0f4f8;
}

/* Filter bar */
.pr-bar{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:14px;}
.pr-bar-info{margin-left:auto;font-size:12px;color:#6b7a93;}
.pr-bar-info strong{color:#166534;}

/* Table */
.pr-tbl-wrap{overflow-x:auto;}
.pr-tbl{width:100%;border-collapse:collapse;font-size:13px;}
.pr-tbl thead th{
  text-align:left;padding:9px 11px;background:#f5f8fb;
  color:#0f2644;font-weight:700;border-bottom:2px solid #e2e8f0;
  font-size:11.5px;white-space:nowrap;
}
.pr-tbl thead th.r{text-align:right;}
.pr-tbl tbody td{padding:10px 11px;border-bottom:1px solid #f0f4f8;vertical-align:middle;}
.pr-tbl tbody tr:last-child td{border-bottom:none;}
.pr-tbl tbody tr:hover td{background:#f8fafc;}
.pr-tbl td.r{text-align:right;} .pr-tbl td.ctr{text-align:center;}
.pr-mono{font-family:'DM Mono',monospace;font-size:12.5px;}
.pr-empty{text-align:center;padding:40px;color:#9ca3af;font-size:13px;}

/* Badges */
.pr-badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;}
.pr-badge.green {background:#dcfce7;color:#166534;}
.pr-badge.blue  {background:#dbeafe;color:#1e40af;}
.pr-badge.orange{background:#ffedd5;color:#9a3412;}
.pr-badge.gray  {background:#f3f4f6;color:#4b5563;}

/* Icon buttons */
.pr-ico{
  display:inline-flex;align-items:center;justify-content:center;
  width:30px;height:30px;border-radius:6px;border:none;
  cursor:pointer;transition:opacity .15s;padding:0;
}
.pr-ico:hover{opacity:.72;}
.pr-ico svg{width:14px;height:14px;fill:currentColor;}
.pr-ico.view{background:#dcfce7;color:#166534;}
.pr-ico.edit{background:#dbeafe;color:#1e40af;}
.pr-ico.del {background:#fee2e2;color:#991b1b;}
.pr-act{display:flex;gap:5px;align-items:center;justify-content:center;}

/* Employee name / sub */
.pr-name{font-weight:700;font-size:13px;}
.pr-sub{font-size:11px;color:#6b7a93;margin-top:1px;}

/* Forms */
.pr-grid {display:grid;grid-template-columns:1fr 1fr;    gap:12px;margin-bottom:12px;}
.pr-grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:12px;}
.pr-fg{display:flex;flex-direction:column;gap:4px;}
.pr-fg label{font-size:12px;font-weight:600;color:#374151;}
.pr-fg input,.pr-fg select,.pr-fg textarea{
  font-size:13px;padding:8px 10px;border:1.5px solid #c8d4e0;
  border-radius:6px;background:#fff;color:#222;outline:none;
  font-family:'DM Sans',sans-serif;transition:border-color .15s;
}
.pr-fg input:focus,.pr-fg select:focus,.pr-fg textarea:focus{border-color:#1a5eb8;}
.pr-fg textarea{resize:vertical;min-height:72px;}
.pr-period-row{display:flex;gap:6px;}
.pr-period-row select{flex:1;}
.pr-actions{display:flex;gap:8px;justify-content:flex-end;margin-top:14px;}

/* Section label */
.pr-sec{
  font-size:11.5px;font-weight:700;color:#0f2644;
  text-transform:uppercase;letter-spacing:.05em;margin:14px 0 8px;
}
.pr-sec.red{color:#c2410c;}

/* Loading spinner */
.pr-spin{
  display:inline-block;width:22px;height:22px;
  border:3px solid #e2e8f0;border-top-color:#1a5eb8;
  border-radius:50%;animation:prSpin .7s linear infinite;
}
@keyframes prSpin{to{transform:rotate(360deg)}}
.pr-loading{display:flex;justify-content:center;padding:40px;}

/* ── Bulletin / Facture preview ──
   §3.15 — Espacements resserrés (marges/paddings réduits sur l'ensemble
   du bulletin, largeur de la carte augmentée) pour une mise en page plus
   compacte à l'impression/export PDF : moins d'espace perdu, meilleure
   utilisation de la page (demande explicite des RH suite à un export PDF
   montrant de larges zones vides). */
.pr-preview{
  background:#fff;border:2px solid #0f2644;border-radius:10px;
  padding:16px;max-width:1000px;margin:0 auto;font-size:12px;
}
.pr-preview-hdr{
  display:grid;grid-template-columns:1fr auto;gap:16px;
  margin-bottom:10px;padding-bottom:8px;border-bottom:2px solid #0f2644;
  align-items:start;
}
.pr-preview-org{font-size:15px;font-weight:700;color:#0f2644;}
.pr-preview-sub{font-size:11px;color:#6b7a93;margin-top:2px;}
.pr-preview-badge{
  text-align:center;font-size:14px;font-weight:700;color:#0f2644;
  padding:7px 20px;background:#e8edf3;border-radius:6px;
}
.pr-preview-period{font-size:12px;color:#4b5563;text-align:center;margin-top:5px;}

.pr-info-grid{
  display:grid;grid-template-columns:1fr 1fr;gap:10px;
  margin-bottom:8px;font-size:12px;align-items:start;
}
.pr-info-block{background:#f5f8fb;border-radius:6px;padding:8px 10px;}
.pr-info-block-title{
  font-weight:700;color:#0f2644;font-size:11px;
  text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px;
}
.pr-info-row{display:flex;gap:4px;margin-bottom:2px;}
.pr-info-key{color:#6b7a93;min-width:148px;flex-shrink:0;}
.pr-info-val{font-weight:500;}

/* Bande de section pleine largeur (ex: "ÉLÉMENTS DE SALAIRE") entre deux
   blocs d'information, alignée sur le style des sec-row du tableau */
.pr-section-band{
  background:#0f2644;color:#fff;font-weight:700;font-size:11px;
  text-transform:uppercase;letter-spacing:.05em;
  padding:6px 12px;border-radius:6px;margin-bottom:8px;
}

/* Bulletin table */
.pr-btbl{width:100%;border-collapse:collapse;margin:6px 0;font-size:11.5px;}
.pr-btbl th{
  background:#0f2644;color:#fff;padding:6px 9px;
  text-align:left;font-size:11px;font-weight:600;
}
.pr-btbl th.r{text-align:right;}
.pr-btbl td{padding:5px 9px;border-bottom:1px solid #f0f4f8;vertical-align:middle;}
.pr-btbl td.r{text-align:right;font-family:'DM Mono',monospace;}
.pr-btbl .sec-row td{background:#f5f8fb;font-weight:700;color:#0f2644;font-size:11px;padding:6px 9px;}
.pr-btbl .tot-row td{background:#dcfce7;font-weight:700;color:#166534;}
.pr-btbl .net-row td{background:#0f2644;color:#fff;font-weight:700;font-size:13px;padding:8px 9px;}
.pr-btbl .chg-row td{background:#fff7ed;}

/* Nouveau format (BULLETIN REVISION.xlsx) — surligne le net à payer en jaune,
   comme dans le fichier source, pour le distinguer visuellement de l'ancien format */
.pr-btbl .net-row-v2 td{background:#FFFF00;color:#0f2644;font-weight:700;font-size:13px;padding:8px 9px;}
.pr-btbl .sub-row td:first-child{padding-left:22px;color:#374151;}

/* Bloc CHARGES SALARIALES (nouveau format) — teinte violette douce et
   distincte du vert utilisé pour les totaux patronaux, pour bien séparer
   visuellement la part employeur de la part employé sur le bulletin */
.pr-btbl .sal-sec-row td{background:#f3e8ff;font-weight:700;color:#6b21a8;font-size:11px;padding:6px 9px;}
.pr-btbl .sal-sub-row td{background:#faf5ff;}
.pr-btbl .sal-sub-row td:first-child{padding-left:22px;color:#581c87;}
.pr-btbl .sal-tot-row td{background:#e9d5ff;font-weight:700;color:#6b21a8;}

/* Toolbar */
.pr-toolbar{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;}
.pr-sig-row{
  display:grid;grid-template-columns:1fr 1fr;gap:20px;
  margin-top:6px;font-size:11px;color:#4b5563;
}
.pr-sig{border-top:1px solid #0f2644;padding-top:7px;text-align:center;}

/* Avatar initials */
.pr-avatar{
  display:inline-flex;align-items:center;justify-content:center;
  width:32px;height:32px;border-radius:50%;font-size:12px;font-weight:700;
  color:#fff;flex-shrink:0;
}

/* Modal overlay */
.pr-modal-overlay{
  position:fixed;inset:0;background:rgba(15,38,68,.45);z-index:1000;
  display:flex;align-items:center;justify-content:center;padding:20px;
}
.pr-modal{
  background:#fff;border-radius:12px;box-shadow:0 20px 60px rgba(0,0,0,.25);
  width:100%;max-width:660px;max-height:90vh;overflow-y:auto;
  display:flex;flex-direction:column;
}
.pr-modal-hdr{
  display:flex;align-items:center;justify-content:space-between;
  padding:16px 20px;border-bottom:2px solid #f0f4f8;
  position:sticky;top:0;background:#fff;z-index:2;
}
.pr-modal-hdr h3{font-size:15px;font-weight:700;color:#0f2644;margin:0;}
.pr-modal-body{padding:20px;}
.pr-modal-close{
  width:30px;height:30px;border-radius:6px;border:none;background:#f3f4f6;
  cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;
  color:#4b5563;
}
.pr-modal-close:hover{background:#e5e7eb;}
.pr-modal-ftr{
  padding:14px 20px;border-top:2px solid #f0f4f8;
  display:flex;gap:8px;justify-content:flex-end;
  position:sticky;bottom:0;background:#fff;z-index:2;
}

@media print{
  .pr-tabs,.pr-kpis,.pr-actions,.pr-toolbar,.pr-header-right,.pr-bar,.pr-modal-overlay,.pr-repart-config{display:none!important;}
  .pr-preview{border:none;max-width:100%;padding:0;}
}

`;

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return <div className={`pr-toast ${type}`}>{msg}</div>;
}

// ─── SVG Icons ───────────────────────────────────────────────────────────────
const IcoEye = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z"/>
  </svg>
);
const IcoDel = () => (
  <svg viewBox="0 0 24 24">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);
const IcoEdit = () => (
  <svg viewBox="0 0 24 24">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
);
const IcoPrint = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT — état global partagé entre tous les onglets
// ═══════════════════════════════════════════════════════════════════════════════
export default function PaiePage() {
  const [filterMois,  setFilterMois]  = useState(now.getMonth() + 1);
  const [filterAnnee, setFilterAnnee] = useState(now.getFullYear());
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  // 'bulletins' | 'factures' | 'new-bulletin' | 'apercu-bulletin' | 'new-facture' | 'apercu-facture'
  const [tab, setTab]       = useState('bulletins');
  const [toast, setToast]   = useState(null);
  const [bulletins, setBulletins] = useState([]);
  const [factures,  setFactures]  = useState([]);
  const [loadingB,  setLoadingB]  = useState(true);
  const [loadingF,  setLoadingF]  = useState(true);

  // viewBul peut être un bulletin chargé depuis l'API (objet riche) OU un objet "preview" construit localement
  const [viewBul, setViewBul] = useState(null);
  const [viewFac, setViewFac] = useState(null);
  const [editBul, setEditBul] = useState(null); // bulletin en cours d'édition
  const [editFac, setEditFac] = useState(null); // facture en cours d'édition
  // Format d'affichage du bulletin consulté : 'ancien' (BULLETIN DE PAIE actuel)
  // ou 'nouveau' (reproduction fidèle de BULLETIN REVISION.xlsx) — bascule
  // disponible via un bouton dans la barre d'outils de l'aperçu.

  const showToast = (msg, type = 'success') => setToast({ msg, type });
  const [generatingMass, setGeneratingMass] = useState(false);

  const loadBulletins = useCallback(async () => {
    setLoadingB(true);
    try { const r = await getBulletins({ mois: filterMois, annee: filterAnnee }); setBulletins(r.data); } catch {}
    setLoadingB(false);
  }, [filterMois, filterAnnee]);

  const loadFactures = useCallback(async () => {
    setLoadingF(true);
    try { const r = await getFactures({ mois: filterMois, annee: filterAnnee }); setFactures(r.data); } catch {}
    setLoadingF(false);
  }, [filterMois, filterAnnee]);

  useEffect(() => { loadBulletins(); loadFactures(); }, [loadBulletins, loadFactures]);

  // KPIs globaux
  const masseBrute    = bulletins.reduce((s, b) => s + parseFloat(b.brut_total || 0), 0);
  const totalPatronal = bulletins.reduce((s, b) => s + parseFloat(b.total_patronal || 0), 0);

  const openBulletinFromList = async id => {
    try { const r = await getBulletin(id); setViewBul(r.data); setTab('apercu-bulletin'); } catch {}
  };
  const openFactureFromList = async id => {
    try { const r = await getFacture(id); setViewFac(r.data); setTab('apercu-facture'); }
    catch (err) { showToast(err.response?.data?.error || ("Impossible d'ouvrir la facture : " + (err.message || 'erreur inconnue')), 'error'); }
  };
  const openEditBulletin = async id => {
    try {
      const r = await getBulletin(id);
      const bul = r.data;
      // Si aucun champ de date d'entrée présent dans le bulletin, on récupère l'employé
      const hasDate = bul.date_debut || bul.date_entree || bul.date_embauche || bul.date_recrutement;
      if (!hasDate && bul.employe_id) {
        try {
          const emps = await getEmployes({ limit: 200 });
          const emp = (emps.data.data || []).find(e => String(e.id) === String(bul.employe_id));
          if (emp) {
            bul.date_debut = emp.date_entree || emp.date_debut || emp.date_embauche || emp.date_recrutement || '';
          }
        } catch {}
      }
      setEditBul(bul);
    } catch {}
  };
  const openEditFacture = async id => {
    try { const r = await getFacture(id); setEditFac(r.data); }
    catch (err) { showToast(err.response?.data?.error || ("Impossible d'ouvrir la facture : " + (err.message || 'erreur inconnue')), 'error'); }
  };

  // ── Export §Livre de paie / Annexe ITS DGI (corrigé §3.7) ──────────────
  // Deux hooks distincts → deux états "exporting" indépendants (un spinner
  // par bouton, l'un ne bloque pas l'autre).
  const { exporting: exportingLivre, exportMultiSheet } = useExportExcel();
  const { exporting: exportingITS,   exportToExcel: exportITSSheet } = useExportExcel();

  /** Reventile les charges patronales CNPS/CMU/FPC à partir du brut total.
   *  §3.7 — Mêmes taux que PaieController::bulletinCreate (alignés sur
   *  BULLETIN REVISION.xlsx) — bulletins_paie ne stocke que cfce_pat
   *  combiné (FDFP Formation + CMU patronale), on le rescinde ici pour
   *  l'affichage détaillé du livre de paie / des impôts à régler.
   *  NB : CNPS PF/AT utilisent désormais un plafond distinct (75 000 FCFA)
   *  de celui de la retraite (1 647 315 FCFA), et la CMU patronale (§3.11)
   *  est désormais calculée par enfant à charge (nbEnfants requis, non
   *  disponible ici sans enrichissement) — le montant réel vient de
   *  bp.cfce_pat déjà persisté ; ce ventilateur reste une estimation
   *  d'affichage pour le livre de paie. */
  const ventilerChargesPatronales = (brutTotal) => {
    const plafondRetraite = Math.min(brutTotal, PAIE_PARAMS.plafondCnpsRetraite);
    const plafondPfAt     = Math.min(brutTotal, PAIE_PARAMS.plafondCnpsPfAt);
    return {
      cnpsPatRetraite: Math.round(plafondRetraite * PAIE_PARAMS.tauxCnpsRetraitePatronal),
      cnpsPatPF:       Math.round(plafondPfAt * PAIE_PARAMS.tauxCnpsPrestationsFamiliales),
      cnpsPatAT:       Math.round(plafondPfAt * PAIE_PARAMS.tauxCnpsAccidentTravail),
      cmuPat:          PAIE_PARAMS.cmuMontantPatronal,
      fpcPat:          Math.round(brutTotal * PAIE_PARAMS.tauxFdfpFormation),
    };
  };

  /** Enrichit chaque bulletin de la liste avec les données bulletin détaillé
   *  (getBulletin → répartition projet, fonction, N°CNPS...) et employé
   *  (getEmploye → téléphone, sexe, nationalité, catégorie, contrat...),
   *  absentes de la liste paginée des bulletins. Appels en parallèle. */
  const enrichirBulletins = async (list) => {
    return Promise.all(list.map(async b => {
      try {
        const [showR, empR] = await Promise.all([
          getBulletin(b.id),
          getEmploye(b.employe_id),
        ]);
        return { ...b, ...showR.data, ...empR.data, id: b.id, bulletin_id: b.id, employe_id: b.employe_id };
      } catch {
        return { ...b };
      }
    }));
  };

  const MAP_SITUATION_FAMILLE = { 'Célibataire': 'C', 'Marié(e)': 'M', 'Divorcé(e)': 'D', 'Veuf(ve)': 'V' };

  /** Génère le Livre de paie (3 feuilles : Salariés / Consultances / Impôts à
   *  régler) pour le mois/année sélectionné, à partir des bulletins et
   *  factures déjà chargés — même structure que le livre de paie de
   *  référence, adaptée au système ITS actuel (au lieu des anciennes
   *  colonnes IS/CN/IGR). */
  const handleGenererMasse = async (mode) => {
    const msg = mode === 'backfill'
      ? "Rattrapage complet : créer les bulletins/factures manquants depuis la date d'embauche de chaque employé actif. L'historique existant NE sera PAS modifié. Cette opération peut prendre plusieurs minutes."
      : `Générer les bulletins/factures de ${MOIS[filterMois]} ${filterAnnee} pour tous les employés actifs. Les documents existants seront régénérés (sauf ceux verrouillés).`;
    if (!window.confirm(msg + "\n\nContinuer ?")) return;
    setGeneratingMass(true);
    try {
      const { data } = await genererBulletinsMasse(
        mode === 'backfill' ? { mode: 'backfill' } : { mode: 'mois', mois: filterMois, annee: filterAnnee }
      );
      const r = data.resultat || {};
      showToast(`Terminé — employés: ${r.employes ?? 0} · créés: ${r.crees ?? 0} · régénérés: ${r.regeneres ?? 0} · ignorés: ${r.ignores ?? 0} · erreurs: ${r.erreurs ?? 0}`);
      loadBulletins(); loadFactures();
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors de la génération en masse', 'error');
    }
    setGeneratingMass(false);
  };

  const handleGenererLivrePaie = async () => {
    if (!bulletins.length) {
      showToast('Aucun bulletin pour cette période — impossible de générer le livre de paie.', 'error');
      return;
    }
    try {
      const enrichis = await enrichirBulletins(bulletins);

      const rowsSalaries = enrichis.map((b, i) => {
        const brutTotal = parseFloat(b.brut_total || 0);
        const { cmuPat, fpcPat } = ventilerChargesPatronales(brutTotal);
        return {
          ordre: i + 1,
          matricule: b.matricule || '',
          nom_complet: `${b.nom || ''} ${b.prenoms || ''}`.trim(),
          telephone: b.telephone || '',
          fonction: b.fonction || '',
          categorie: b.categorie || '',
          type_contrat: b.type_contrat || '',
          date_debut: b.date_debut || '',
          anciennete: calcAnciennete(b.date_debut, filterMois, filterAnnee),
          situation_matrimoniale: b.situation_matrimoniale || '',
          num_cnps: b.num_cnps || '',
          salaire_base: parseFloat(b.salaire_base || 0),
          sursalaire: parseFloat(b.sursalaire || 0),
          transport: parseFloat(b.transport || 0),
          logement: parseFloat(b.logement || 0),
          brut_total: brutTotal,
          cnps_sal: parseFloat(b.cnps_sal || 0),
          its: parseFloat(b.its || 0),
          total_retenues: parseFloat(b.total_retenues || 0),
          net_a_payer: parseFloat(b.net_a_payer || 0),
          cnps_pat: parseFloat(b.cnps_pat || 0),
          cmu_pat: cmuPat,
          fpc_pat: fpcPat,
          ta_pat: parseFloat(b.ta_pat || 0),
          total_patronal: parseFloat(b.total_patronal || 0),
          // §3.7 — avec la formule Excel (net = brut − (charges pat.+sal.)),
          // le coût total réel = net + total_retenues (qui contient déjà les
          // deux volets patronal ET salarial) = brut_total − trop_perçu.
          masse_salariale: parseFloat(b.net_a_payer || 0) + parseFloat(b.total_retenues || 0),
          projet_nom: b.projet_nom || b.bailleur || '',
        };
      });

      const rowsConsultances = factures.map((f, i) => ({
        ordre: i + 1,
        matricule: f.matricule || '',
        nom_complet: `${f.nom || ''} ${f.prenoms || ''}`.trim(),
        fonction: f.fonction || '',
        num_facture: f.num_facture || '',
        projet_nom: f.projet_nom || '',
        montant_brut: parseFloat(f.montant_brut || 0),
        retenue_fiscale: parseFloat(f.retenue_fiscale || 0),
        net_a_payer: parseFloat(f.net_a_payer || 0),
      }));

      const totCnpsSal = enrichis.reduce((s, b) => s + parseFloat(b.cnps_sal || 0), 0);
      const totCnpsPat = enrichis.reduce((s, b) => s + parseFloat(b.cnps_pat || 0), 0);
      const totIts     = enrichis.reduce((s, b) => s + parseFloat(b.its || 0), 0);
      const totTaPat   = enrichis.reduce((s, b) => s + parseFloat(b.ta_pat || 0), 0);
      const totFpcPat  = enrichis.reduce((s, b) => s + ventilerChargesPatronales(parseFloat(b.brut_total || 0)).fpcPat, 0);
      const totCmuPat  = enrichis.reduce((s, b) => s + ventilerChargesPatronales(parseFloat(b.brut_total || 0)).cmuPat, 0);
      const totBnc     = factures.reduce((s, f) => s + parseFloat(f.retenue_fiscale || 0), 0);

      const rowsImpots = [
        { libelle: 'CNPS à régler (salarial + patronal)', montant: totCnpsSal + totCnpsPat },
        { libelle: 'FDFP à régler (TA + FPC)',             montant: totTaPat + totFpcPat },
        { libelle: 'ITS à régler',                          montant: totIts },
        { libelle: 'CMU patronale à régler',                montant: totCmuPat },
        { libelle: 'BNC 7,5 % (consultants)',               montant: totBnc },
        { libelle: 'TOTAL CHARGES À RÉGLER',                montant: totCnpsSal + totCnpsPat + totTaPat + totFpcPat + totIts + totCmuPat + totBnc },
      ];

      const moisLabel = MOIS[filterMois];
      await exportMultiSheet({
        filename: `GFM3_LivreDePaie_${filterAnnee}_${String(filterMois).padStart(2, '0')}.xlsx`,
        sheets: [
          {
            sheetName: 'Salariés',
            title: `LIVRE DE PAIE GFM3 — ${moisLabel} ${filterAnnee}`,
            rows: rowsSalaries,
            columns: [
              { key: 'ordre',                  label: 'N° Ordre',               width: 9  },
              { key: 'matricule',              label: 'Matricule',              width: 12 },
              { key: 'nom_complet',            label: 'Nom & Prénoms',          width: 28 },
              { key: 'telephone',              label: 'Contact',                width: 16 },
              { key: 'fonction',               label: 'Fonction',               width: 24 },
              { key: 'categorie',              label: 'Catégorie',              width: 14 },
              { key: 'type_contrat',           label: 'Contrat',                width: 10 },
              { key: 'date_debut',             label: 'Début contrat',          width: 14, format: v => fmtDate(v) },
              { key: 'anciennete',             label: 'Ancienneté (ans)',       width: 13 },
              { key: 'situation_matrimoniale', label: 'État civil',             width: 14 },
              { key: 'num_cnps',               label: 'N° CNPS',                width: 16 },
              { key: 'salaire_base',           label: 'Salaire de base',        width: 15 },
              { key: 'sursalaire',             label: 'Sursalaire',             width: 13 },
              { key: 'transport',              label: 'Transport',              width: 12 },
              { key: 'logement',               label: 'Logement',               width: 12 },
              { key: 'brut_total',             label: 'Brut total',             width: 14 },
              { key: 'cnps_sal',               label: 'CNPS salarié',           width: 13 },
              { key: 'its',                    label: 'ITS',                    width: 12 },
              { key: 'total_retenues',         label: 'Total retenues (sal.+pat.)', width: 18 },
              { key: 'net_a_payer',            label: 'Net à payer',            width: 14 },
              { key: 'cnps_pat',               label: 'CNPS patronal',          width: 14 },
              { key: 'cmu_pat',                label: 'CMU patronale',          width: 13 },
              { key: 'fpc_pat',                label: 'FPC',                    width: 10 },
              { key: 'ta_pat',                 label: "Taxe d'apprentissage",   width: 16 },
              { key: 'total_patronal',         label: 'Total charges patronales', width: 18 },
              { key: 'masse_salariale',        label: 'Masse salariale',        width: 16 },
              { key: 'projet_nom',             label: 'Projet / Bailleur',      width: 22 },
            ],
          },
          {
            sheetName: 'Consultances',
            title: `ÉTAT DES CONSULTANCES — ${moisLabel} ${filterAnnee}`,
            rows: rowsConsultances,
            columns: [
              { key: 'ordre',           label: 'N° Ordre',        width: 9  },
              { key: 'matricule',       label: 'Matricule',       width: 12 },
              { key: 'nom_complet',     label: 'Nom & Prénoms',   width: 28 },
              { key: 'fonction',        label: 'Fonction',        width: 22 },
              { key: 'num_facture',     label: 'N° Facture',      width: 12 },
              { key: 'projet_nom',      label: 'Projet / Bailleur', width: 22 },
              { key: 'montant_brut',    label: 'Honoraire brut',  width: 15 },
              { key: 'retenue_fiscale', label: 'Retenue 7,5 %',   width: 13 },
              { key: 'net_a_payer',     label: 'Net à payer',     width: 14 },
            ],
          },
          {
            sheetName: 'Impôts à régler',
            title: `CHARGES DE LA PÉRIODE À RÉGLER — ${moisLabel} ${filterAnnee}`,
            rows: rowsImpots,
            columns: [
              { key: 'libelle', label: 'Nature de la charge', width: 34 },
              { key: 'montant', label: 'Montant (FCFA)',      width: 18 },
            ],
          },
        ],
      });
      showToast('Livre de paie généré avec succès.');
    } catch (e) {
      showToast('Erreur lors de la génération du livre de paie.', 'error');
    }
  };

  /** Pré-remplit l'onglet SAISIE de l'annexe ITS (État 301) officielle DGI,
   *  au format colonnes attendu, pour copier/coller dans le fichier .xlsm
   *  officiel (workflow documenté dans l'onglet "A SAVOIR" du modèle DGI).
   *  Champs non suivis en base (code emploi, nombre d'enfants à charge,
   *  nombre de parts, RICF) sont laissés vides — à compléter manuellement
   *  avant transmission, comme convenu. */
  const handleGenererAnnexeITS = async () => {
    if (!bulletins.length) {
      showToast('Aucun bulletin pour cette période — impossible de préparer l’annexe ITS.', 'error');
      return;
    }
    try {
      const enrichis = await enrichirBulletins(bulletins);
      const rows = enrichis.map((b, i) => {
        // §3.7 — inclut la prime d'ancienneté (imposable) ; transport/logement/
        // indemnité de représentation restent hors assiette (non imposables)
        const brutImposable       = parseFloat(b.salaire_base || 0) + parseFloat(b.sursalaire || 0) + parseFloat(b.prime_anciennete || 0);
        const revenusNonImposables = parseFloat(b.transport || 0) + parseFloat(b.logement || 0) + parseFloat(b.indemnite_representation || 0);
        return {
          ordre: i + 1,
          num_cnps: b.num_cnps || '',
          nom_complet: `${b.nom || ''} ${b.prenoms || ''}`.trim(),
          type_travailleur: 'Salarié',
          emploi: b.fonction || '',
          code_emploi: '',
          regime: 'G',
          sexe: b.sexe || '',
          nationalite: b.nationalite || '',
          local_expatrie: 'L',
          situation_famille: MAP_SITUATION_FAMILLE[b.situation_matrimoniale] || '',
          nb_enfants: '',
          nb_parts: '',
          nb_jours: 30,
          montant_salaires: brutImposable,
          avantages_bareme: 0,
          avantages_reels: 0,
          remuneration_brute: brutImposable + revenusNonImposables,
          revenus_non_imposables: revenusNonImposables,
          remuneration_imposable: brutImposable,
          ricf: 0,
          its_brut: parseFloat(b.its || 0),
          its_net: parseFloat(b.its || 0),
          ajustement: 0,
          net_a_payer: parseFloat(b.its || 0),
          indemnite_montant: revenusNonImposables,
          indemnite_designation: 'Transport / Logement',
        };
      });

      await exportITSSheet({
        filename: `GFM3_AnnexeITS_SAISIE_${filterAnnee}_${String(filterMois).padStart(2, '0')}.xlsx`,
        sheetName: 'SAISIE',
        title: `Pré-remplissage annexe ITS (État 301) — ${MOIS[filterMois]} ${filterAnnee} — à compléter puis coller dans le modèle officiel DGI`,
        rows,
        columns: [
          { key: 'ordre',                   label: '#',                                    width: 6  },
          { key: 'num_cnps',                label: 'N° CNPS',                              width: 16 },
          { key: 'nom_complet',             label: 'Nom et prénoms',                       width: 28 },
          { key: 'type_travailleur',        label: 'Type de travailleur',                  width: 14 },
          { key: 'emploi',                  label: 'Emploi ou Qualité',                    width: 24 },
          { key: 'code_emploi',             label: 'Code Emploi (à compléter)',            width: 16 },
          { key: 'regime',                  label: 'Régime (G/A)',                         width: 10 },
          { key: 'sexe',                    label: 'Sexe',                                 width: 8  },
          { key: 'nationalite',             label: 'Nationalité',                          width: 12 },
          { key: 'local_expatrie',          label: 'Local/Expatrié',                       width: 12 },
          { key: 'situation_famille',       label: 'Situation de famille',                 width: 14 },
          { key: 'nb_enfants',              label: 'Nbr enfants à charge (à compléter)',   width: 14 },
          { key: 'nb_parts',                label: 'Nombre de parts (à compléter)',        width: 14 },
          { key: 'nb_jours',                label: 'Jours',                                width: 8  },
          { key: 'montant_salaires',        label: 'Salaires & accessoires',               width: 16 },
          { key: 'avantages_bareme',        label: 'Avantages (barème)',                   width: 14 },
          { key: 'avantages_reels',         label: 'Avantages (réel)',                     width: 14 },
          { key: 'remuneration_brute',      label: 'Rémunération brute',                   width: 16 },
          { key: 'revenus_non_imposables',  label: 'Revenus non imposables',               width: 16 },
          { key: 'remuneration_imposable',  label: 'Rémunération imposable',                width: 16 },
          { key: 'ricf',                    label: 'RICF (à compléter)',                   width: 12 },
          { key: 'its_brut',                label: 'ITS Brut',                             width: 12 },
          { key: 'its_net',                 label: 'ITS Net',                              width: 12 },
          { key: 'ajustement',              label: 'Ajustement',                           width: 10 },
          { key: 'net_a_payer',             label: 'Net à payer (ITS)',                    width: 14 },
          { key: 'indemnite_montant',       label: 'Indemnités exonérées (montant)',       width: 16 },
          { key: 'indemnite_designation',   label: 'Indemnités exonérées (désignation)',   width: 22 },
        ],
      });
      showToast('Annexe ITS pré-remplie générée. Complétez code emploi / enfants / parts / RICF puis collez ces lignes dans SAISIE (modèle officiel DGI).');
    } catch (e) {
      showToast("Erreur lors de la génération de l'annexe ITS.", 'error');
    }
  };

  return (
    <div className="pr">
      <style>{STYLES}</style>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {editBul && (
        <EditBulletinModal
          bul={editBul}
          years={years}
          onClose={() => setEditBul(null)}
          onSaved={() => { setEditBul(null); loadBulletins(); showToast('Bulletin mis à jour'); }}
          showToast={showToast}
        />
      )}
      {editFac && (
        <EditFactureModal
          fac={editFac}
          years={years}
          onClose={() => setEditFac(null)}
          onSaved={() => { setEditFac(null); loadFactures(); showToast('Facture mise à jour'); }}
          showToast={showToast}
        />
      )}

      {/* ── Header global ── */}
      <div className="pr-header">
        <div>
          <h2>Paie &amp; Rémunérations</h2>
          <p>Bulletins de paie, factures de consultance</p>
        </div>
        <div className="pr-header-right">
          <select className="pr-sel" value={filterMois}  onChange={e => setFilterMois(+e.target.value)}>
            {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <select className="pr-sel" value={filterAnnee} onChange={e => setFilterAnnee(+e.target.value)}>
            {years.map(y => <option key={y}>{y}</option>)}
          </select>
          <button className="pr-btn green" onClick={() => setTab('new-bulletin')}>+ Nouveau bulletin</button>
          <button className="pr-btn blue"  onClick={() => setTab('new-facture')}>+ Facture consultance</button>
          <button
            className="pr-btn"
            style={{ background: '#0F766E', color: '#fff' }}
            disabled={exportingLivre}
            onClick={handleGenererLivrePaie}
            title="Générer le livre de paie (Salariés / Consultances / Impôts à régler) pour la période sélectionnée"
          >
            {exportingLivre ? '⏳ Génération…' : '📘 Livre de paie'}
          </button>
          <button
            className="pr-btn"
            style={{ background: '#7C3AED', color: '#fff' }}
            disabled={exportingITS}
            onClick={handleGenererAnnexeITS}
            title="Pré-remplir l'onglet SAISIE de l'annexe ITS (État 301) à coller dans le modèle officiel DGI"
          >
            {exportingITS ? '⏳ Génération…' : '🧾 Annexe ITS (DGI)'}
          </button>
          <button className="pr-btn" style={{ background: '#B45309', color: '#fff' }} disabled={generatingMass}
            onClick={() => handleGenererMasse('mois')} title="Générer les bulletins/factures de tous les employés actifs pour le mois sélectionné">
            {generatingMass ? '⏳ Génération…' : '⚙️ Générer tous (mois)'}
          </button>
          <button className="pr-btn" style={{ background: '#92400E', color: '#fff' }} disabled={generatingMass}
            onClick={() => handleGenererMasse('backfill')} title="Rattrapage : créer tous les documents manquants depuis la date d'embauche">
            {generatingMass ? '⏳…' : '⏱️ Rattrapage'}
          </button>
        </div>
      </div>

      {/* ── KPI cards globaux ── */}
      <div className="pr-kpis">
        <div className="pr-kpi">
          <div className="pr-kpi-label">Masse salariale brute</div>
          <div className="pr-kpi-val blue">{fmt(masseBrute)} FCFA</div>
        </div>
        <div className="pr-kpi">
          <div className="pr-kpi-label">Total bulletins</div>
          <div className="pr-kpi-val green">{bulletins.length}</div>
        </div>
        <div className="pr-kpi">
          <div className="pr-kpi-label">Total factures</div>
          <div className="pr-kpi-val orange">{factures.length}</div>
        </div>
        <div className="pr-kpi">
          <div className="pr-kpi-label">Charges patronales</div>
          <div className="pr-kpi-val red">{fmt(totalPatronal)} FCFA</div>
        </div>
      </div>

      {/* ── Onglets (niveau unique) ── */}
      <div className="pr-tabs">
        <button className={`pr-tab${tab === 'bulletins'        ? ' active' : ''}`} onClick={() => setTab('bulletins')}>Bulletins de paie</button>
        <button className={`pr-tab${tab === 'factures'         ? ' active' : ''}`} onClick={() => setTab('factures')}>Factures de consultance</button>
        <button className={`pr-tab${tab === 'factures-presta'  ? ' active' : ''}`} onClick={() => setTab('factures-presta')}>Factures de prestation de service</button>
        <button className={`pr-tab${tab === 'new-bulletin'     ? ' active' : ''}`} onClick={() => setTab('new-bulletin')}>Nouveau bulletin</button>
        {viewBul && <button className={`pr-tab${tab === 'apercu-bulletin' ? ' active' : ''}`} onClick={() => setTab('apercu-bulletin')}>Aperçu bulletin</button>}
        {viewFac && <button className={`pr-tab${tab === 'apercu-facture'  ? ' active' : ''}`} onClick={() => setTab('apercu-facture')}>Aperçu facture</button>}
      </div>

      {/* ── Contenu ── */}
      {tab === 'bulletins' && (
        <BulletinsList
          bulletins={bulletins} loading={loadingB}
          loadBulletins={loadBulletins} openBulletin={openBulletinFromList}
          openEditBulletin={openEditBulletin}
          years={years}
          filterMois={filterMois} filterAnnee={filterAnnee}
          setFilterMois={setFilterMois} setFilterAnnee={setFilterAnnee}
        />
      )}
      {tab === 'factures' && (
        <FacturesList
          variante="consultance"
          factures={factures} loading={loadingF}
          loadFactures={loadFactures} openFacture={openFactureFromList}
          openEditFacture={openEditFacture}
          years={years}
          filterMois={filterMois} filterAnnee={filterAnnee}
          setFilterMois={setFilterMois} setFilterAnnee={setFilterAnnee}
        />
      )}
      {tab === 'factures-presta' && (
        <FacturesList
          variante="prestation"
          factures={factures} loading={loadingF}
          loadFactures={loadFactures} openFacture={openFactureFromList}
          openEditFacture={openEditFacture}
          years={years}
          filterMois={filterMois} filterAnnee={filterAnnee}
          setFilterMois={setFilterMois} setFilterAnnee={setFilterAnnee}
        />
      )}
      {tab === 'new-bulletin' && (
        <NouveauBulletin
          showToast={showToast} years={years}
          filterMois={filterMois} filterAnnee={filterAnnee}
          onPreview={bul => { setViewBul(bul); setTab('apercu-bulletin'); }}
          onSaved={() => { loadBulletins(); setTab('bulletins'); }}
          onCancel={() => setTab('bulletins')}
        />
      )}
      {tab === 'new-facture' && (
        <NouvelleFacture
          showToast={showToast} years={years}
          filterMois={filterMois} filterAnnee={filterAnnee}
          onSaved={() => { loadFactures(); setTab('factures'); }}
          onCancel={() => setTab('factures')}
        />
      )}
      {tab === 'apercu-bulletin' && viewBul && (
        <ApercuBulletinV2
          bul={viewBul}
          onBack={() => setTab(viewBul._preview ? 'new-bulletin' : 'bulletins')}
          showToast={showToast}
          onSaved={loadBulletins}
        />
      )}
      {tab === 'apercu-facture' && viewFac && (
        <ApercuFacture fac={viewFac} onBack={() => setTab('factures')} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LISTE BULLETINS
// ═══════════════════════════════════════════════════════════════════════════════

// ── Hook tri tableau ──────────────────────────────────────────
function useSortTable(data, defaultCol = '') {
  const [sortCol, setSortCol] = React.useState(defaultCol);
  const [sortDir, setSortDir] = React.useState('asc');
  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sorted = React.useMemo(() => {
    if (!sortCol || !data) return data || [];
    return [...data].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol];
      const na = parseFloat(va), nb = parseFloat(vb);
      const numOk = !isNaN(na) && !isNaN(nb) && String(va).trim() !== '' && String(vb).trim() !== '';
      const cmp = numOk ? (na - nb) : (va ?? '').toString().localeCompare((vb ?? '').toString(), 'fr', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortCol, sortDir]);
  const Th = ({ col, label, style = {}, className = '' }) => (
    <th onClick={() => handleSort(col)}
        className={className}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
        title={`Trier par ${label}`}>
      {label}{' '}
      {sortCol === col
        ? (sortDir === 'asc' ? '▲' : '▼')
        : <span style={{ opacity: 0.25 }}>⇅</span>}
    </th>
  );
  return { sorted, Th, sortCol, sortDir };
}

function BulletinsList({ bulletins, loading, loadBulletins, openBulletin, openEditBulletin, years, filterMois, filterAnnee, setFilterMois, setFilterAnnee }) {
  const { sorted: sortedBulletins, Th: ThB } = useSortTable(bulletins);
  const [rechB, setRechB] = React.useState('');
  const visibleBulletins = sortedBulletins.filter(b => {
    const s = rechB.trim().toLowerCase();
    return !s || (`${b.nom || ''} ${b.prenoms || ''} ${b.matricule || ''} ${b.categorie || ''} ${b.type_contrat || ''}`).toLowerCase().includes(s);
  });
  const masseSal    = bulletins.reduce((s, b) => s + parseFloat(b.net_a_payer  || 0), 0);
  const masseCharge = bulletins.reduce((s, b) => s + parseFloat(b.total_patronal || 0), 0);

  return (
    <div className="pr-card">
      <div className="pr-bar">
        <select className="pr-sel" value={filterMois}  onChange={e => setFilterMois(+e.target.value)}>
          {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select className="pr-sel" value={filterAnnee} onChange={e => setFilterAnnee(+e.target.value)}>
          {years.map(y => <option key={y}>{y}</option>)}
        </select>
        <input className="pr-sel" style={{ minWidth: 240, flex: '0 1 260px' }} placeholder="🔍 Rechercher (nom, matricule…)" value={rechB} onChange={e => setRechB(e.target.value)} />
        <span className="pr-bar-info">
          Masse nette : <strong>{fmt(masseSal)} FCFA</strong>
          &nbsp;·&nbsp;Charges pat. : <strong style={{ color: '#c2410c' }}>{fmt(masseCharge)} FCFA</strong>
        </span>
      </div>

      <div className="pr-tbl-wrap">
        {loading
          ? <div className="pr-loading"><span className="pr-spin" /></div>
          : (
            <table className="pr-tbl">
              <thead>
                <tr>
                  <ThB col="nom"        label="Employé(e)" />
                  <ThB col="matricule"  label="Matricule" />
                  <ThB col="categorie"  label="Catégorie" />
                  <ThB col="type_contrat" label="Contrat" />
                  <th className="r">Salaire brut</th>
                  <th className="r" title="Retenues salariales + charges patronales, conformément à BULLETIN REVISION.xlsx">Retenues (sal.+pat.)</th>
                  <ThB col="net_a_payer"    label="Net à payer"    style={{ textAlign:'right' }} />
                  <th className="r">Charges patron.</th>
                  <ThB col="mois"       label="Période" />
                  <th style={{ textAlign: 'center', minWidth: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bulletins.length === 0
                  ? <tr><td colSpan={9} className="pr-empty">Aucun bulletin pour cette période</td></tr>
                  : visibleBulletins.map(b => {
                    const initials = ((b.nom||'').charAt(0) + (b.prenoms||'').charAt(0)).toUpperCase();
                    const colors = ['#1a5eb8','#16a34a','#c2410c','#7c3aed','#0891b2','#b45309'];
                    const color  = colors[(b.nom||'A').charCodeAt(0) % colors.length];
                    return (
                    <tr key={b.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="pr-avatar" style={{ background: color }}>{initials}</span>
                          <div>
                            <div className="pr-name">{b.nom} {b.prenoms}</div>
                            {b.fonction && <div className="pr-sub">{b.fonction}</div>}
                          </div>
                        </div>
                      </td>
                      <td><span className="pr-mono" style={{ color: '#1a5eb8' }}>{b.matricule}</span></td>
                      <td style={{ fontSize: 12 }}>{b.categorie || '—'}</td>
                      <td>
                        <span className={`pr-badge ${b.type_contrat === 'CDI' ? 'green' : b.type_contrat === 'CDD' ? 'blue' : 'gray'}`}>
                          {b.type_contrat || b.statut || '—'}
                        </span>
                      </td>
                      <td className="r pr-mono">{fmt(b.brut_total)}</td>
                      <td className="r pr-mono" style={{ color: '#c2410c' }}>{fmt(b.total_retenues)}</td>
                      <td className="r pr-mono" style={{ color: '#166534', fontWeight: 700 }}>{fmt(b.net_a_payer)}</td>
                      <td className="r pr-mono" style={{ color: '#c2410c' }}>{fmt(b.total_patronal)}</td>
                      <td style={{ fontSize: 12, color: '#4b5563' }}>{MOIS[b.mois]} {b.annee}</td>
                      <td className="ctr">
                        <div className="pr-act">
                          <button className="pr-ico view" title="Aperçu"    onClick={() => openBulletin(b.id)}><IcoEye /></button>
                          <button className="pr-ico edit" title="Modifier"  onClick={() => openEditBulletin(b.id)}><IcoEdit /></button>
                          <button className="pr-ico del"  title="Supprimer"
                            onClick={() => window.confirm('Supprimer ce bulletin ?') && deleteBulletin(b.id).then(loadBulletins)}>
                            <IcoDel />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LISTE FACTURES
// ═══════════════════════════════════════════════════════════════════════════════
/* §3.56 — Une facture relève de la PRESTATION DE SERVICE si le contrat est
   « Prestataire de service » ou « Bénévole » (retenue PPSI 2 %), sinon de la
   CONSULTANCE (retenue BNC 7,5 %). Repli sur le taux de retenue lorsque le
   type de contrat n'est pas renseigné. */
export function estPrestation(fac) {
  const t = (fac.type_contrat || '').toLowerCase();
  if (t) {
    return t.includes('prestat') || t.includes('bénévol') || t.includes('benevol');
  }
  const brut = parseFloat(fac.montant_brut || 0);
  const ret  = parseFloat(fac.retenue_fiscale || 0);
  return brut > 0 && (ret / brut) < 0.05;   // 2 % → prestation, 7,5 % → consultance
}

function FacturesList({ factures, loading, loadFactures, openFacture, openEditFacture, years, filterMois, filterAnnee, setFilterMois, setFilterAnnee, variante = 'consultance' }) {
  const estPresta = variante === 'prestation';
  const facturesType = React.useMemo(
    () => factures.filter(f => estPrestation(f) === estPresta),
    [factures, estPresta]
  );
  const { sorted: sortedFactures, Th: ThF } = useSortTable(facturesType);
  const [rechF, setRechF] = React.useState('');
  const visibleFactures = sortedFactures.filter(fac => {
    const s = rechF.trim().toLowerCase();
    return !s || (`${fac.num_facture || ''} ${fac.nom || ''} ${fac.prenoms || ''} ${fac.email || ''}`).toLowerCase().includes(s);
  });
  const totalBrut = facturesType.reduce((s, f) => s + parseFloat(f.montant_brut   || 0), 0);
  const totalNet  = facturesType.reduce((s, f) => s + parseFloat(f.net_a_payer    || 0), 0);
  const totalRet  = facturesType.reduce((s, f) => s + parseFloat(f.retenue_fiscale|| 0), 0);

  return (
    <div className="pr-card">
      <div className="pr-bar">
        <select className="pr-sel" value={filterMois}  onChange={e => setFilterMois(+e.target.value)}>
          {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select className="pr-sel" value={filterAnnee} onChange={e => setFilterAnnee(+e.target.value)}>
          {years.map(y => <option key={y}>{y}</option>)}
        </select>
        <input className="pr-sel" style={{ minWidth: 240, flex: '0 1 260px' }} placeholder="🔍 Rechercher (nom, n° facture…)" value={rechF} onChange={e => setRechF(e.target.value)} />
        <span className="pr-bar-info">
          <strong style={{ color: estPresta ? '#7c3aed' : '#1a5eb8' }}>
            {estPresta ? 'Prestation de service — PPSI 2 %' : 'Consultance — BNC 7,5 %'}
          </strong>
          &nbsp;·&nbsp;{facturesType.length} facture{facturesType.length > 1 ? 's' : ''}
          &nbsp;·&nbsp;Brut : <strong>{fmt(totalBrut)} FCFA</strong>
          &nbsp;·&nbsp;Retenues : <strong style={{ color: '#c2410c' }}>{fmt(totalRet)} FCFA</strong>
          &nbsp;·&nbsp;Net : <strong>{fmt(totalNet)} FCFA</strong>
        </span>
      </div>

      <div className="pr-tbl-wrap">
        {loading
          ? <div className="pr-loading"><span className="pr-spin" /></div>
          : (
            <table className="pr-tbl">
              <thead>
                <tr>
                  <ThF col="num_facture" label="N° Facture" />
                  <ThF col="nom" label={estPresta ? 'Prestataire' : 'Consultant(e)'} />
                  <th>Fonction</th>
                  <th>Site</th>
                  <ThF col="montant_brut" label="Montant brut" style={{ textAlign: 'right' }} />
                  <th className="r">Retenue</th>
                  <ThF col="net_a_payer" label="Net a payer" style={{ textAlign: 'right' }} />
                  <ThF col="mois" label="Periode" />
                  <th>Projet</th>
                  <th style={{ textAlign: 'center', minWidth: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {facturesType.length === 0
                  ? <tr><td colSpan={10} className="pr-empty">
                      Aucune facture de {estPresta ? 'prestation de service' : 'consultance'} pour cette période
                    </td></tr>
                  : visibleFactures.map(fac => (
                    <tr key={fac.id}>
                      <td><span className="pr-mono" style={{ fontWeight: 700, color: '#1a5eb8' }}>{fac.num_facture}</span></td>
                      <td>
                        <div className="pr-name">{fac.nom} {fac.prenoms}</div>
                        {fac.email && <div className="pr-sub">{fac.email}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{fac.fonction || '—'}</td>
                      <td style={{ fontSize: 12 }}>{fac.site || fac.lieu || '—'}</td>
                      <td className="r pr-mono">{fmt(fac.montant_brut)}</td>
                      <td className="r pr-mono" style={{ color: '#c2410c' }}>{fmt(fac.retenue_fiscale)}</td>
                      <td className="r pr-mono" style={{ color: '#166534', fontWeight: 700 }}>{fmt(fac.net_a_payer)}</td>
                      <td style={{ fontSize: 12, color: '#4b5563' }}>{MOIS[fac.mois]} {fac.annee}</td>
                      <td style={{ fontSize: 12 }}>{fac.projet_nom || '—'}</td>
                      <td className="ctr">
                        <div className="pr-act">
                          <button className="pr-ico view" title="Aperçu"   onClick={() => openFacture(fac.id)}><IcoEye /></button>
                          <button className="pr-ico edit" title="Modifier" onClick={() => openEditFacture(fac.id)}><IcoEdit /></button>
                          <button className="pr-ico del"  title="Supprimer"
                            onClick={() => window.confirm('Supprimer cette facture ?') && deleteFacture(fac.id).then(loadFactures)}>
                            <IcoDel />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// NOUVEAU BULLETIN
// ═══════════════════════════════════════════════════════════════════════════════
function NouveauBulletin({ showToast, years, filterMois, filterAnnee, onPreview, onSaved, onCancel }) {
  const [employes, setEmployes] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({
    employe_id: '', mois: filterMois, annee: filterAnnee,
    salaire_base: 0, sursalaire: 0, indemnite_rep: 0,
    transport: 75000, logement: 0, trop_percu: 0, jours_travailles: 30,
    anciennete: 0, nb_enfants: 0, matrimonial: 'Célibataire',
    type_contrat: 'CDI', matricule: '', cnps: '', categorie: '',
    departement: '', fonction: '', date_debut: '',
  });
  const [selectedEmp, setSelectedEmp] = useState(null);

  useEffect(() => {
    getEmployes({ statut: 'Actif', limit: 200 }).then(r => setEmployes(r.data.data || []));
  }, []);

  const f = k => e => {
    const val = e.target.value;
    setForm(p => {
      const next = { ...p, [k]: val };
      // Recalcul auto ancienneté si période ou date_debut change
      if ((k === 'mois' || k === 'annee') && next.date_debut) {
        next.anciennete = calcAnciennete(next.date_debut, parseInt(next.mois), parseInt(next.annee));
      }
      return next;
    });
  };

  const handleSelectEmp = async e => {
    const empId = e.target.value;
    // Trouver l'employé dans la liste pour affichage immédiat
    const empBase = employes.find(x => String(x.id) === String(empId));
    setSelectedEmp(empBase || null);

    if (!empId) {
      setForm(p => ({ ...p, employe_id: '' }));
      return;
    }

    // Pré-remplissage immédiat avec les données de la liste
    setForm(p => ({
      ...p,
      employe_id:   empId,
      matricule:    empBase?.matricule    || '',
      type_contrat: empBase?.type_contrat || 'CDI',
      departement:  empBase?.departement  || '',
      fonction:     empBase?.fonction     || '',
      salaire_base: empBase?.salaire_base || 0,
    }));

    // Note : le pré-remplissage complet de sursalaire / indemnité de
    // représentation / transport (§3.13, saisis désormais sur la fiche
    // employé) se fait ci-dessous une fois la fiche complète chargée via
    // getEmploye(), car ces champs ne sont pas présents dans la liste
    // légère renvoyée par getEmployes().

    // Chargement de la fiche complète pour les champs enrichis
    try {
      const r = await getEmploye(parseInt(empId));
      const emp = r.data;
      setSelectedEmp(emp);
      console.log('[NouveauBulletin] Fiche complète :', emp);

      const dateDebut = emp.date_debut || emp.date_entree || emp.date_embauche || emp.date_recrutement || '';
      const anc = calcAnciennete(dateDebut, parseInt(form.mois), parseInt(form.annee));
      const nbEnfants = Array.isArray(emp.enfants) ? emp.enfants.length : (parseInt(emp.nb_enfants) || 0);

      setForm(p => ({
        ...p,
        employe_id:   String(emp.id),
        matricule:    emp.matricule                || '',
        cnps:         emp.num_cnps                 || '',   // ← num_cnps depuis la table employes
        categorie:    emp.categorie                || emp.fonction || '',  // ← fonction comme fallback catégorie
        salaire_base: emp.salaire_base             || 0,
        // §3.13 — Sursalaire / Indemnité de représentation / Transport sont
        // désormais saisis une fois pour toutes sur la fiche employé (onglet
        // Infos RH) et repris automatiquement ici à chaque création de
        // bulletin ; toujours modifiables au cas par cas ci-dessous.
        sursalaire:    emp.sursalaire               || 0,
        indemnite_rep: emp.indemnite_representation || 0,
        transport:     emp.indemnite_transport      || 0,
        type_contrat: emp.type_contrat             || 'CDI',
        departement:  emp.departement              || '',
        fonction:     emp.fonction                 || '',
        date_debut:   dateDebut,
        anciennete:   anc,
        nb_enfants:   nbEnfants,                           // ← compte les enfants depuis la table enfants
        matrimonial:  emp.situation_matrimoniale   || 'Célibataire',
      }));
    } catch (err) {
      console.error('[NouveauBulletin] Erreur chargement fiche :', err);
    }
  };

  // Calcul live
  const C = computeCharges(form);

  /** Construit un objet bulletin "preview" sans passer par l'API */
  const buildPreviewBulletin = () => {
    const emp = selectedEmp;
    return {
      _preview: true,
      prenoms:  emp?.prenoms || '—',
      nom:      emp?.nom     || '—',
      matricule: form.matricule,
      num_cnps:  form.cnps,
      contact:   emp?.telephone || emp?.contact || '',
      categorie: form.categorie,
      fonction:  form.fonction,
      departement: form.departement,
      situation_matrimoniale: form.matrimonial,
      nombre_parts: C.parts,
      nb_enfants: C.nbEnfants,
      type_contrat: form.type_contrat,
      date_debut: form.date_debut,
      anciennete: parseFloat(form.anciennete) || 0,
      taux_anciennete_pct: Math.round(C.ancPct * 100 * 100) / 100,
      jours_travailles: C.joursTravailles,
      mois: parseInt(form.mois),
      annee: parseInt(form.annee),
      indemnite_representation: C.rep,
      transport: C.transp,
      logement: C.loge,
      trop_percu: C.tropPercu,
      salaire_base: C.base,
      sursalaire: C.sursal,
      brut_total: C.brutImpos,
      total_retenues: C.totRet + C.totPat,
      total_patronal: C.totPat,
      net_a_payer: C.net,
      lignes: [
        { type_ligne: 'gain', libelle: 'Salaire catégoriel', base: C.base, taux: 0, montant: C.base },
        C.sursal > 0 && { type_ligne: 'gain', libelle: 'Sursalaire', base: C.sursal, taux: 0, montant: C.sursal },
        C.ancMnt > 0 && { type_ligne: 'gain', libelle: `Prime d'ancienneté (${(C.ancPct * 100).toFixed(2)}%)`, base: C.base, taux: C.ancPct, montant: C.ancMnt },
        C.rep > 0 && { type_ligne: 'gain', libelle: 'Indemnité de représentation', base: 0, taux: 0, montant: C.rep },
        C.loge > 0 && { type_ligne: 'gain', libelle: 'Indemnité de logement', base: 0, taux: 0, montant: C.loge },
        { type_ligne: 'retenue', libelle: `CNPS – Retraite (salarié ${(PAIE_PARAMS.tauxCnpsRetraiteSalarie*100).toFixed(1)}%)`, base: C.brutImpos, taux: PAIE_PARAMS.tauxCnpsRetraiteSalarie, montant: C.cnpsSal },
        { type_ligne: 'retenue', libelle: `CMU – Couverture Maladie (salariale, ${PAIE_PARAMS.cmuMontantSalarial} × ${C.cmuPersonnes} pers.)`, base: 0, taux: 0, montant: C.cmuSal },
        { type_ligne: 'retenue', libelle: `Impôt sur les charges familiales (ICF, ${C.parts} part(s))`, base: C.brutImpos, taux: 0, montant: Math.round(C.icf) },
        C.ricf > 0 && { type_ligne: 'retenue', libelle: "Réduction d'impôt sur charges familiales (RICF)", base: 0, taux: 0, montant: -Math.round(C.ricf) },
        C.tropPercu > 0 && { type_ligne: 'retenue', libelle: 'Trop-perçu / déductions', base: 0, taux: 0, montant: C.tropPercu },
        { type_ligne: 'charge_pat', libelle: `CNPS – Retraite patronal (${(PAIE_PARAMS.tauxCnpsRetraitePatronal*100).toFixed(1)}%)`, base: Math.min(C.brutImpos, PAIE_PARAMS.plafondCnpsRetraite), taux_pat: PAIE_PARAMS.tauxCnpsRetraitePatronal, montant_pat: C.cnpsPatRetraite, montant: C.cnpsPatRetraite },
        { type_ligne: 'charge_pat', libelle: `CNPS – Prestations Familiales (${(PAIE_PARAMS.tauxCnpsPrestationsFamiliales*100).toFixed(2)}%)`, base: Math.min(C.brutImpos, PAIE_PARAMS.plafondCnpsPfAt), taux_pat: PAIE_PARAMS.tauxCnpsPrestationsFamiliales, montant_pat: C.cnpsPatPF, montant: C.cnpsPatPF },
        { type_ligne: 'charge_pat', libelle: `CNPS – Accidents du Travail (${(PAIE_PARAMS.tauxCnpsAccidentTravail*100).toFixed(0)}%)`, base: Math.min(C.brutImpos, PAIE_PARAMS.plafondCnpsPfAt), taux_pat: PAIE_PARAMS.tauxCnpsAccidentTravail, montant_pat: C.cnpsPatAT, montant: C.cnpsPatAT },
        { type_ligne: 'charge_pat', libelle: `FDFP – Taxe d'Apprentissage (${(PAIE_PARAMS.tauxFdfpApprentissage*100).toFixed(1)}%)`, base: C.brutImpos, taux_pat: PAIE_PARAMS.tauxFdfpApprentissage, montant_pat: C.fdfpApprent, montant: C.fdfpApprent },
        { type_ligne: 'charge_pat', libelle: `FDFP – Formation Professionnelle Continue (${(PAIE_PARAMS.tauxFdfpFormation*100).toFixed(1)}%)`, base: C.brutImpos, taux_pat: PAIE_PARAMS.tauxFdfpFormation, montant_pat: C.fdfpFormation, montant: C.fdfpFormation },
        { type_ligne: 'charge_pat', libelle: `CMU – Couverture Maladie (patronale, ${PAIE_PARAMS.cmuMontantPatronal} × ${C.cmuPersonnes} pers.)`, base: 0, taux_pat: 0, montant_pat: C.cmuPat, montant: C.cmuPat },
        { type_ligne: 'charge_pat', libelle: `IS – Impôt sur les Salaires (${(PAIE_PARAMS.tauxImpotSalaires*100).toFixed(1)}%)`, base: C.brutImpos, taux_pat: PAIE_PARAMS.tauxImpotSalaires, montant_pat: C.impotSalaires, montant: C.impotSalaires },
      ].filter(Boolean),
    };
  };

  const handleCreate = async () => {
    if (!form.employe_id || !form.salaire_base) { showToast('Employé et salaire requis', 'error'); return; }
    setSaving(true);
    try {
      // §3.7 — mapping vers les clés attendues par PaieController::bulletinCreate()
      await createBulletin({
        ...form,
        anciennete_annees:        parseFloat(form.anciennete) || 0,
        indemnite_representation: parseFloat(form.indemnite_rep) || 0,
        jours_travailles:         parseFloat(form.jours_travailles) || 30,
      });
      showToast('Bulletin généré avec succès');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors de la génération', 'error');
    }
    setSaving(false);
  };

  return (
    <>
      {/* ── Informations employé ── */}
      <div className="pr-card">
        <div className="pr-card-title">Saisie du bulletin de paie</div>

        <div className="pr-fg" style={{ marginBottom: 12 }}>
          <label>Employé(e) *</label>
          <select value={form.employe_id} onChange={handleSelectEmp}>
            <option value="">— Sélectionner —</option>
            {employes.map(e => <option key={e.id} value={e.id}>{e.nom} {e.prenoms} ({e.matricule})</option>)}
          </select>
        </div>

        <div className="pr-grid3">
          <div className="pr-fg">
            <label>Matricule</label>
            <input value={form.matricule} onChange={f('matricule')} />
          </div>
          <div className="pr-fg">
            <label>N° CNPS</label>
            <input value={form.cnps} onChange={f('cnps')} />
          </div>
          <div className="pr-fg">
            <label>Catégorie / Emploi occupé</label>
            <input value={form.categorie} onChange={f('categorie')} placeholder="ex: Cadre 10 C" />
          </div>
        </div>

        <div className="pr-grid3">
          <div className="pr-fg">
            <label>Type de contrat</label>
            <select value={form.type_contrat} onChange={f('type_contrat')}>
              <option>CDI</option><option>CDD</option><option>Stage</option>
            </select>
          </div>
          <div className="pr-fg">
            <label>Situation matrimoniale</label>
            <select value={form.matrimonial} onChange={f('matrimonial')}>
              <option>Célibataire</option><option>Marié(e)</option><option>Divorcé(e)</option><option>Veuf(ve)</option>
            </select>
          </div>
          <div className="pr-fg">
            <label>Période</label>
            <div className="pr-period-row">
              <select value={form.mois} onChange={f('mois')}>
                {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <select value={form.annee} onChange={f('annee')} style={{ width: 90 }}>
                {years.map(y => <option key={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="pr-grid">
          <div className="pr-fg">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Nombre d'enfants
              {form.employe_id && <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', borderRadius: 20,
                padding: '1px 7px', fontWeight: 700 }}>AUTO</span>}
            </label>
            <input
              type="number" min="0" max="20"
              value={form.nb_enfants}
              onChange={f('nb_enfants')}
              style={form.employe_id ? { background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#166534', fontWeight: 700 } : {}}
              title={form.employe_id ? 'Récupéré automatiquement depuis la fiche employé (modifiable)' : ''}
            />
          </div>
          <div className="pr-fg">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Ancienneté (années)
              <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', borderRadius: 20,
                padding: '1px 7px', fontWeight: 700 }}>AUTO</span>
            </label>
            <input readOnly value={form.anciennete}
              style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#166534', fontWeight: 700 }}
              title={form.date_debut ? `Calculé depuis le ${new Date(form.date_debut).toLocaleDateString('fr-FR')}` : 'Sélectionnez un employé'} />
            {form.date_debut && (
              <span style={{ fontSize: 10, color: '#6b7a93', marginTop: 2 }}>
                Entrée : {new Date(form.date_debut).toLocaleDateString('fr-FR')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Éléments de salaire ── */}
      <div className="pr-card">
        <div className="pr-card-title">Éléments de salaire</div>
        <div className="pr-grid3">
          <div className="pr-fg">
            <label>Salaire catégoriel (FCFA) *</label>
            <input type="number" min="0" value={form.salaire_base} onChange={f('salaire_base')} />
          </div>
          <div className="pr-fg">
            <label>Sursalaire (FCFA)</label>
            <input type="number" min="0" value={form.sursalaire} onChange={f('sursalaire')} />
          </div>
          <div className="pr-fg">
            <label>Indemnité de représentation (FCFA)</label>
            <input type="number" min="0" value={form.indemnite_rep} onChange={f('indemnite_rep')} />
          </div>
        </div>
        <div className="pr-grid3">
          <div className="pr-fg">
            <label>Indemnité de transport (FCFA)</label>
            <input type="number" min="0" value={form.transport} onChange={f('transport')} />
          </div>
          <div className="pr-fg">
            <label>Logement (FCFA)</label>
            <input type="number" min="0" value={form.logement} onChange={f('logement')} />
          </div>
          <div className="pr-fg">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Jours travaillés
              <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', borderRadius: 20,
                padding: '1px 7px', fontWeight: 700 }}>/30</span>
            </label>
            <input type="number" min="0" max="31" value={form.jours_travailles} onChange={f('jours_travailles')}
              title="Nombre de jours travaillés sur la période — le brut imposable est proratisé sur 30" />
          </div>
        </div>
        <div className="pr-grid">
          <div className="pr-fg">
            <label>Trop perçu à déduire (FCFA)</label>
            <input type="number" min="0" value={form.trop_percu} onChange={f('trop_percu')} />
          </div>
        </div>
      </div>

      {/* ── Résultat de calcul en temps réel ── */}
      {C.base > 0 && (
        <div className="pr-card">
          <div className="pr-card-title">Résultat de calcul (Côte d'Ivoire — conforme BULLETIN REVISION.xlsx)</div>

          {/* Mini KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
            {[
              ['Brut imposable',     fmt(C.brutImpos) + ' FCFA', '#1a5eb8'],
              ['Retenues salarié',   fmt(C.totRet)    + ' FCFA', '#991b1b'],
              ['Net à payer',        fmt(C.net)        + ' FCFA', '#166534'],
              ['Charges patronales', fmt(C.totPat)    + ' FCFA', '#c2410c'],
            ].map(([lbl, val, color], i) => (
              <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#6b7a93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>{lbl}</div>
                <div style={{ fontSize: 17, fontWeight: 700, color }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Gauche : brut + retenues */}
            <div>
              <div className="pr-sec">BRUT IMPOSABLE (Salarié)</div>
              <table className="pr-btbl">
                <tbody>
                  <tr><td>Salaire catégoriel</td><td className="r">{fmt(C.base)}</td></tr>
                  <tr><td>Sursalaire</td><td className="r">{fmt(C.sursal)}</td></tr>
                  <tr><td>Prime d'ancienneté ({(C.ancPct * 100).toFixed(2)}%)</td><td className="r">{fmt(C.ancMnt)}</td></tr>
                  {C.joursTravailles !== 30 && <tr><td style={{ fontStyle: 'italic', color: '#6b7a93' }}>Proratisé sur {C.joursTravailles}/30 jours</td><td className="r">—</td></tr>}
                  <tr className="tot-row"><td><strong>BRUT IMPOSABLE</strong></td><td className="r"><strong>{fmt(C.brutImpos)}</strong></td></tr>
                </tbody>
              </table>

              <div className="pr-sec red" style={{ marginTop: 14 }}>RETENUES SALARIÉ</div>
              <table className="pr-btbl">
                <tbody>
                  <tr><td>CNPS – Retraite (salarié {(PAIE_PARAMS.tauxCnpsRetraiteSalarie*100).toFixed(1)}%)</td><td className="r" style={{ color: '#c2410c' }}>{fmt(C.cnpsSal)}</td></tr>
                  <tr><td>CMU (salariale, {PAIE_PARAMS.cmuMontantSalarial} × {C.cmuPersonnes} pers.)</td><td className="r" style={{ color: '#c2410c' }}>{fmt(C.cmuSal)}</td></tr>
                  <tr><td>ICF ({C.parts} part(s))</td><td className="r" style={{ color: '#c2410c' }}>{fmt(C.icf)}</td></tr>
                  {C.ricf > 0 && <tr><td>RICF (réduction charges familiales)</td><td className="r" style={{ color: '#15803d' }}>−{fmt(C.ricf)}</td></tr>}
                  {C.tropPercu > 0 && <tr><td>Trop perçu</td><td className="r" style={{ color: '#c2410c' }}>{fmt(C.tropPercu)}</td></tr>}
                  <tr className="tot-row">
                    <td><strong>Total retenues salariales</strong></td>
                    <td className="r"><strong style={{ color: '#991b1b' }}>{fmt(C.totRet)}</strong></td>
                  </tr>
                </tbody>
              </table>

              <table className="pr-btbl" style={{ marginTop: 4 }}>
                <tbody>
                  <tr><td>Brut imposable − (retenues salariales + charges patronales)</td><td className="r">{fmt(C.brutImpos - C.totRet - C.totPat)}</td></tr>
                  {C.transp > 0 && <tr><td>+ Indemnité transport</td><td className="r">{fmt(C.transp)}</td></tr>}
                  {C.rep    > 0 && <tr><td>+ Indemnité représentation</td><td className="r">{fmt(C.rep)}</td></tr>}
                  {C.loge   > 0 && <tr><td>+ Logement</td><td className="r">{fmt(C.loge)}</td></tr>}
                  {C.tropPercu > 0 && <tr><td>− Trop perçu</td><td className="r">−{fmt(C.tropPercu)}</td></tr>}
                  <tr className="net-row">
                    <td><strong>NET À PAYER</strong></td>
                    <td className="r"><strong>{fmt(C.net)} FCFA</strong></td>
                  </tr>
                </tbody>
              </table>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
                Formule conforme à BULLETIN REVISION.xlsx : le net déduit à la fois les charges salariales et patronales du brut imposable.
              </div>
            </div>

            {/* Droite : charges patronales */}
            <div>
              <div className="pr-sec red">CHARGES PATRONALES</div>
              <table className="pr-btbl">
                <tbody>
                  {[
                    [`CNPS – Retraite patronal (${(PAIE_PARAMS.tauxCnpsRetraitePatronal*100).toFixed(1)}%)`, C.cnpsPatRetraite],
                    [`CNPS – Prestations Familiales (${(PAIE_PARAMS.tauxCnpsPrestationsFamiliales*100).toFixed(2)}%)`, C.cnpsPatPF],
                    [`CNPS – Accidents du Travail (${(PAIE_PARAMS.tauxCnpsAccidentTravail*100).toFixed(0)}%)`, C.cnpsPatAT],
                    [`FDFP – Taxe d'Apprentissage (${(PAIE_PARAMS.tauxFdfpApprentissage*100).toFixed(1)}%)`, C.fdfpApprent],
                    [`FDFP – Formation Prof. Continue (${(PAIE_PARAMS.tauxFdfpFormation*100).toFixed(1)}%)`, C.fdfpFormation],
                    [`CMU (patronale, ${PAIE_PARAMS.cmuMontantPatronal} × ${C.cmuPersonnes} pers.)`, C.cmuPat],
                    [`IS – Impôt sur les Salaires (${(PAIE_PARAMS.tauxImpotSalaires*100).toFixed(1)}%)`, C.impotSalaires],
                  ].map(([lbl, val], i) => (
                    <tr key={i} className="chg-row">
                      <td>{lbl}</td>
                      <td className="r" style={{ color: '#c2410c' }}>{fmt(val)}</td>
                    </tr>
                  ))}
                  <tr className="tot-row">
                    <td><strong>Total charges patronales</strong></td>
                    <td className="r"><strong style={{ color: '#c2410c' }}>{fmt(C.totPat)}</strong></td>
                  </tr>
                  <tr style={{ background: '#fff7ed' }}>
                    <td style={{ fontWeight: 700, color: '#c2410c', padding: '6px 9px' }}>Coût total employeur (brut + indemnités)</td>
                    <td className="r" style={{ fontWeight: 700, color: '#c2410c', padding: '6px 9px' }}>{fmt(C.brutTotal - C.tropPercu)}</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
                Avec la formule BULLETIN REVISION.xlsx, les charges patronales sont déduites du net de l'employé plutôt que
                facturées en plus — le coût total employeur reste donc égal au brut + indemnités (non affecté par ce choix).
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pr-actions">
        <button className="pr-btn gray"  onClick={onCancel}>Annuler</button>
        <button className="pr-btn dark"  onClick={() => onPreview(buildPreviewBulletin())} disabled={!form.employe_id || !form.salaire_base}>
          Aperçu bulletin
        </button>
        <button className="pr-btn green" onClick={handleCreate} disabled={saving}>
          {saving ? '⏳ Génération…' : '⚙️ Générer le bulletin'}
        </button>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// NOUVELLE FACTURE
// ═══════════════════════════════════════════════════════════════════════════════
function NouvelleFacture({ showToast, years, filterMois, filterAnnee, onSaved, onCancel }) {
  const [employes, setEmployes] = useState([]);
  const [projets,  setProjets]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({
    num_facture: '', employe_id: '', banque: '', projet_id: '',
    mois: filterMois, annee: filterAnnee,
    description: '', temps_mois: 1, montant_brut: 0,
  });

  useEffect(() => {
    getEmployes({ statut: 'Actif', limit: 200 }).then(r => setEmployes(r.data.data || []));
    getProjets().then(r => setProjets(r.data));
  }, []);

  const f    = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const brut = parseFloat(form.montant_brut) || 0;
  const ret  = Math.round(brut * 0.075);
  const net  = brut - ret;

  const handleCreate = async () => {
    if (!form.employe_id || !form.montant_brut) { showToast('Consultant et montant requis', 'error'); return; }
    setSaving(true);
    try {
      await createFacture(form);
      showToast('Facture créée avec succès');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors de la création', 'error');
    }
    setSaving(false);
  };

  return (
    <>
      <div className="pr-card">
        <div className="pr-card-title">Informations de la facture</div>
        <div className="pr-grid">
          <div className="pr-fg">
            <label>N° Facture *</label>
            <input value={form.num_facture} onChange={f('num_facture')} placeholder="FACT-2026-001" />
          </div>
          <div className="pr-fg">
            <label>Consultant *</label>
            <select value={form.employe_id} onChange={f('employe_id')}>
              <option value="">— Sélectionner —</option>
              {employes.map(e => <option key={e.id} value={e.id}>{e.nom} {e.prenoms} ({e.matricule})</option>)}
            </select>
          </div>
        </div>
        <div className="pr-grid">
          <div className="pr-fg">
            <label>Projet</label>
            <select value={form.projet_id} onChange={f('projet_id')}>
              <option value="">— Sans projet —</option>
              {projets.map(p => <option key={p.id} value={p.id}>{p.nom} ({p.partenaire})</option>)}
            </select>
          </div>
          <div className="pr-fg">
            <label>Banque</label>
            <input value={form.banque} onChange={f('banque')} placeholder="Ex: Ecobank CI" />
          </div>
        </div>
        <div className="pr-grid3">
          <div className="pr-fg">
            <label>Mois</label>
            <select value={form.mois} onChange={f('mois')}>
              {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="pr-fg">
            <label>Année</label>
            <select value={form.annee} onChange={f('annee')}>
              {years.map(y => <option key={y}>{y}</option>)}
            </select>
          </div>
          <div className="pr-fg">
            <label>Fraction du mois (ex: 0,5)</label>
            <input type="number" min="0" max="1" step="0.1" value={form.temps_mois} onChange={f('temps_mois')} />
          </div>
        </div>
        <div className="pr-fg" style={{ marginBottom: 10 }}>
          <label>Description des prestations</label>
          <textarea value={form.description} onChange={f('description')} placeholder="Décrivez les prestations réalisées…" />
        </div>
        <div className="pr-fg">
          <label>Montant brut des honoraires (FCFA) *</label>
          <input type="number" min="0" value={form.montant_brut} onChange={f('montant_brut')} />
        </div>
      </div>

      {brut > 0 && (
        <div className="pr-card">
          <div className="pr-card-title">Récapitulatif financier</div>
          <table className="pr-btbl">
            <tbody>
              <tr>
                <td>Montant brut des honoraires</td>
                <td className="r"><strong>{fmt(brut)} FCFA</strong></td>
              </tr>
              <tr className="chg-row">
                <td>Retenue à la source (7,5%)</td>
                <td className="r" style={{ color: '#c2410c' }}>− {fmt(ret)} FCFA</td>
              </tr>
              <tr className="net-row">
                <td><strong>NET À PAYER AU CONSULTANT</strong></td>
                <td className="r"><strong>{fmt(net)} FCFA</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="pr-actions">
        <button className="pr-btn gray"  onClick={onCancel}>Annuler</button>
        <button className="pr-btn green" onClick={handleCreate} disabled={saving}>
          {saving ? '⏳ Création…' : '💾 Créer la facture'}
        </button>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODAL — MODIFIER BULLETIN
// ═══════════════════════════════════════════════════════════════════════════════
function EditBulletinModal({ bul, years, onClose, onSaved, showToast }) {
  const [saving, setSaving] = useState(false);
  const dateDebut = bul.date_debut || bul.date_entree || bul.date_embauche || bul.date_recrutement || '';
  const [form, setForm] = useState(() => {
    const mois = bul.mois;
    const annee = bul.annee;
    const anc = dateDebut ? calcAnciennete(dateDebut, mois, annee) : (bul.anciennete || 0);
    return {
      salaire_base:  bul.salaire_base  || 0,
      sursalaire:    bul.sursalaire    || 0,
      indemnite_rep: bul.indemnite_representation || 0,
      transport:     bul.transport     || 0,
      logement:      bul.logement      || 0,
      trop_percu:    bul.trop_percu    || 0,
      jours_travailles: bul.jours_travailles || 30,
      anciennete:    anc,
      nb_enfants:    bul.nb_enfants    || 0,
      matrimonial:   bul.situation_matrimoniale || 'Célibataire',
      type_contrat:  bul.type_contrat  || 'CDI',
      categorie:     bul.categorie     || '',
      departement:   bul.departement   || '',
      fonction:      bul.fonction      || '',
      mois,
      annee,
      statut:        bul.statut        || 'Brouillon',
    };
  });

  // Recalcul ancienneté si dateDebut arrive après le premier render (chargement async employé)
  useEffect(() => {
    if (dateDebut) {
      setForm(p => ({
        ...p,
        anciennete: calcAnciennete(dateDebut, parseInt(p.mois), parseInt(p.annee)),
      }));
    }
  }, [dateDebut]);

  const f = k => e => {
    const val = e.target.value;
    setForm(p => {
      const next = { ...p, [k]: val };
      // Recalcul auto ancienneté si la période change
      if ((k === 'mois' || k === 'annee') && dateDebut) {
        next.anciennete = calcAnciennete(dateDebut, parseInt(next.mois), parseInt(next.annee));
      }
      return next;
    });
  };
  const C = computeCharges(form);

  const handleSave = async () => {
    setSaving(true);
    try {
      await deleteBulletin(bul.id);
      // §3.7 — mapping vers les clés attendues par PaieController::bulletinCreate()
      await createBulletin({
        ...bul, ...form,
        employe_id: bul.employe_id,
        anciennete_annees:        parseFloat(form.anciennete) || 0,
        indemnite_representation: parseFloat(form.indemnite_rep) || 0,
        jours_travailles:         parseFloat(form.jours_travailles) || 30,
      });
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors de la mise à jour', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="pr-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pr-modal" style={{ maxWidth: 780 }}>

        {/* ── Header foncé ── */}
        <div className="pr-modal-hdr" style={{ background: '#0f2644', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ color: '#fff', fontSize: 16 }}>
            <span style={{ marginRight: 8 }}>✏️</span>Modifier le bulletin de paie
          </h3>
          <button className="pr-modal-close" onClick={onClose}
            style={{ background: 'rgba(255,255,255,.15)', color: '#fff', fontSize: 18, fontWeight: 700 }}>✕</button>
        </div>

        <div className="pr-modal-body">

          {/* Ligne 1 : Employé(e) | Matricule | N° CNPS */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Employé(e)</label>
              <input value={`${bul.nom || ''} ${bul.prenoms || ''}`.trim()} readOnly
                style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
            <div className="pr-fg">
              <label>Matricule</label>
              <input value={bul.matricule || ''} readOnly style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
            <div className="pr-fg">
              <label>N° CNPS</label>
              <input value={bul.num_cnps || ''} readOnly style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
          </div>

          {/* Ligne 2 : Type de contrat | Catégorie/Emploi | Situation matrimoniale */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Type de contrat</label>
              <input value={form.type_contrat} readOnly style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
            <div className="pr-fg">
              <label>Catégorie / Emploi</label>
              <input value={form.categorie} onChange={f('categorie')} placeholder="Ex: Maîtrise 5 A" />
            </div>
            <div className="pr-fg">
              <label>Situation matrimoniale</label>
              <select value={form.matrimonial} onChange={f('matrimonial')}>
                <option>Célibataire</option><option>Marié(e)</option><option>Divorcé(e)</option><option>Veuf(ve)</option>
              </select>
            </div>
          </div>

          {/* Ligne 3 : Nombre d'enfants | Ancienneté (auto) | Période */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Nombre d'enfants</label>
              <input type="number" min="0" value={form.nb_enfants} onChange={f('nb_enfants')} />
            </div>
            <div className="pr-fg">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Ancienneté (années)
                <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', borderRadius: 20,
                  padding: '1px 7px', fontWeight: 700 }}>AUTO</span>
              </label>
              <input readOnly value={form.anciennete}
                style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#166534', fontWeight: 700 }}
                title={dateDebut ? `Calculé depuis le ${new Date(dateDebut).toLocaleDateString('fr-FR')}` : 'Date d\'entrée non renseignée'} />
              {dateDebut && (
                <span style={{ fontSize: 10, color: '#6b7a93', marginTop: 2 }}>
                  Entrée : {new Date(dateDebut).toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>
            <div className="pr-fg">
              <label>Période</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={form.mois} onChange={f('mois')} style={{ flex: 1 }}>
                  {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
                <input type="number" value={form.annee} onChange={f('annee')}
                  style={{ width: 72, padding: '8px 6px', border: '1.5px solid #c8d4e0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
              </div>
            </div>
          </div>

          {/* Séparateur Éléments de salaire */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f2644', margin: '14px 0 10px',
            paddingBottom: 6, borderBottom: '2px solid #e2e8f0' }}>
            Éléments de salaire
          </div>

          {/* Ligne 4 : Salaire catégoriel | Sursalaire | Indemnité représentation */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Salaire catégoriel (FCFA)</label>
              <input type="number" min="0" value={form.salaire_base} onChange={f('salaire_base')} />
            </div>
            <div className="pr-fg">
              <label>Sursalaire (FCFA)</label>
              <input type="number" min="0" value={form.sursalaire} onChange={f('sursalaire')} />
            </div>
            <div className="pr-fg">
              <label>Indemnité représentation (FCFA)</label>
              <input type="number" min="0" value={form.indemnite_rep} onChange={f('indemnite_rep')} />
            </div>
          </div>

          {/* Ligne 5 : Transport | Trop perçu | Jours travaillés */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Indemnité transport (FCFA)</label>
              <input type="number" min="0" value={form.transport} onChange={f('transport')} />
            </div>
            <div className="pr-fg">
              <label>Trop perçu à déduire (FCFA)</label>
              <input type="number" min="0" value={form.trop_percu} onChange={f('trop_percu')} />
            </div>
            <div className="pr-fg">
              <label>Jours travaillés (/30)</label>
              <input type="number" min="0" max="31" value={form.jours_travailles} onChange={f('jours_travailles')} />
            </div>
          </div>

          {/* Bande récapitulative (4 colonnes comme la capture) */}
          {parseFloat(form.salaire_base) > 0 && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0,
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
              overflow: 'hidden', marginTop: 10, textAlign: 'center'
            }}>
              {[
                ['Brut total',       fmt(C.brutImpos), '#1a5eb8'],
                ['Retenues salarié', fmt(C.totRet),    '#c2410c'],
                ['Net à payer',      fmt(C.net),       '#166534'],
                ['Charges patron.',  fmt(C.totPat),    '#c2410c'],
              ].map(([lbl, val, color], i) => (
                <div key={lbl} style={{ padding: '12px 10px', borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none' }}>
                  <div style={{ fontSize: 11, color: '#6b7a93', fontWeight: 500, marginBottom: 4 }}>{lbl}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: "'DM Mono',monospace" }}>{val}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="pr-modal-ftr">
          <button className="pr-btn gray" onClick={onClose}>Annuler</button>
          <button className="pr-btn green" onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Sauvegarde…' : '💾 Enregistrer les modifications'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODAL — MODIFIER FACTURE
// ═══════════════════════════════════════════════════════════════════════════════
function EditFactureModal({ fac, years, onClose, onSaved, showToast }) {
  const [saving, setSaving] = useState(false);
  const [projets, setProjets] = useState([]);
  const [form, setForm] = useState({
    num_facture:   fac.num_facture   || '',
    banque:        fac.banque        || '',
    projet_id:     fac.projet_id     || '',
    mois:          fac.mois,
    annee:         fac.annee,
    description:   fac.description   || '',
    temps_mois:    fac.temps_mois    || 1,
    montant_brut:  fac.montant_brut  || 0,
    site:          fac.site || fac.lieu || '',
    partenaire:    fac.partenaire    || '',
    subvention:    fac.subvention    || '',
    budget_code:   fac.budget_code   || '',
  });

  useEffect(() => {
    getProjets().then(r => setProjets(r.data)).catch(() => {});
  }, []);

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const brut = parseFloat(form.montant_brut) || 0;
  const ret  = Math.round(brut * 0.075);
  const net  = brut - ret;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateFacture(fac.id, form);
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors de la mise à jour', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="pr-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pr-modal" style={{ maxWidth: 780 }}>

        {/* ── Header ── */}
        <div className="pr-modal-hdr" style={{ background: '#0f2644', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ color: '#fff', fontSize: 16 }}>Modifier la facture de consultance</h3>
          <button className="pr-modal-close" onClick={onClose}
            style={{ background: 'rgba(255,255,255,.15)', color: '#fff', fontSize: 18, fontWeight: 700 }}>✕</button>
        </div>

        <div className="pr-modal-body">

          {/* Ligne 1 : Consultant | N° Facture | Matricule */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Consultant(e)</label>
              <input value={`${fac.nom || ''} ${fac.prenoms || ''}`.trim()} readOnly
                style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
            <div className="pr-fg">
              <label>N° Facture</label>
              <input value={form.num_facture} onChange={f('num_facture')} />
            </div>
            <div className="pr-fg">
              <label>Matricule</label>
              <input value={fac.matricule || ''} readOnly style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
          </div>

          {/* Ligne 2 : Fonction | Contact | Email */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Fonction</label>
              <input value={fac.fonction || ''} readOnly style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
            <div className="pr-fg">
              <label>Contact</label>
              <input value={fac.telephone || fac.contact || ''} readOnly style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
            <div className="pr-fg">
              <label>Email</label>
              <input value={fac.email || ''} readOnly style={{ background: '#f5f8fb', color: '#6b7a93' }} />
            </div>
          </div>

          {/* Ligne 3 : Site/Lieu | Banque | Organisation partenaire */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Site / Lieu</label>
              <input value={form.site} onChange={f('site')} placeholder="Ex: Gagnoa" />
            </div>
            <div className="pr-fg">
              <label>Banque</label>
              <input value={form.banque} onChange={f('banque')} placeholder="Ex: NSIA" />
            </div>
            <div className="pr-fg">
              <label>Organisation partenaire</label>
              <input value={form.partenaire} onChange={f('partenaire')} placeholder="Ex: USAID" />
            </div>
          </div>

          {/* Ligne 4 : Titre projet | N° sous-subvention | Budget Code */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Titre du projet</label>
              <select value={form.projet_id} onChange={f('projet_id')}>
                <option value="">— Sans projet —</option>
                {projets.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </div>
            <div className="pr-fg">
              <label>N° sous-subvention</label>
              <input value={form.subvention} onChange={f('subvention')} placeholder="Ex: SUB-2000" />
            </div>
            <div className="pr-fg">
              <label>Budget Code</label>
              <input value={form.budget_code} onChange={f('budget_code')} placeholder="Ex: BC-IMP-714" />
            </div>
          </div>

          {/* Ligne 5 : Description | Temps de travail | Periode */}
          <div className="pr-grid3">
            <div className="pr-fg">
              <label>Description</label>
              <input value={form.description} onChange={f('description')} placeholder="Ex: Honoraire de consultance mois de…" />
            </div>
            <div className="pr-fg">
              <label>Temps de travail (mois)</label>
              <input type="number" min="0" max="1" step="0.1" value={form.temps_mois} onChange={f('temps_mois')} />
            </div>
            <div className="pr-fg">
              <label>Periode</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={form.mois} onChange={f('mois')} style={{ flex: 1 }}>
                  {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
                <input type="number" value={form.annee} onChange={f('annee')}
                  style={{ width: 72, padding: '8px 6px', border: '1.5px solid #c8d4e0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
              </div>
            </div>
          </div>

          {/* Ligne 6 : Montant brut | Retenue (auto) | Net (auto) */}
          <div className="pr-grid3" style={{ alignItems: 'flex-end' }}>
            <div className="pr-fg">
              <label>Montant brut (FCFA)</label>
              <input type="number" min="0" value={form.montant_brut} onChange={f('montant_brut')} />
            </div>
            <div className="pr-fg">
              <label style={{ color: '#c2410c', fontWeight: 700 }}>Retenue fiscale 7,5% (auto)</label>
              <input readOnly value={fmt(ret)}
                style={{ background: '#fff1f2', border: '1.5px solid #fecdd3', color: '#991b1b', fontWeight: 700, fontSize: 15 }} />
            </div>
            <div className="pr-fg">
              <label style={{ color: '#166534', fontWeight: 700 }}>Net a payer (auto)</label>
              <input readOnly value={fmt(net)}
                style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#166534', fontWeight: 700, fontSize: 15 }} />
            </div>
          </div>

          {/* Résumé chiffré */}
          {brut > 0 && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 0,
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
              overflow: 'hidden', marginTop: 6, textAlign: 'center'
            }}>
              {[
                ['Montant brut',   fmt(brut), '#1a5eb8'],
                ['Retenue (7,5%)', fmt(ret),  '#c2410c'],
                ['Net a payer',    fmt(net),  '#166534'],
              ].map(([lbl, val, color], i) => (
                <div key={lbl} style={{ padding: '12px 16px', borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none' }}>
                  <div style={{ fontSize: 11, color: '#6b7a93', fontWeight: 600, marginBottom: 4 }}>{lbl}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: "'DM Mono',monospace" }}>{val}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="pr-modal-ftr">
          <button className="pr-btn gray" onClick={onClose}>Annuler</button>
          <button className="pr-btn green" onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Sauvegarde…' : 'Enregistrer les modifications'}
          </button>
        </div>
      </div>
    </div>
  );
}
/** §3.9 — Repli utilisé uniquement pour les bulletins hérités sans lignes
 *  détaillées en base (avant l'existence de la table lignes_bulletin).
 *  Tout nouveau bulletin passe par PaieController::bulletinCreate() et
 *  arrive ici avec bul.lignes déjà rempli — cette fonction n'est donc
 *  qu'un filet de sécurité, aligné sur les mêmes taux que le moteur actuel
 *  (seul le brut total est proratisé, pas les lignes de détail). §3.25 —
 *  CMU (salariale ET patronale) = montant unitaire × cmuPersonnes
 *  (employé(e) + conjoint(e) si marié(e) + enfants à charge). */
function calcLignesBulletin(bul) {
  const brut = parseFloat(bul.salaire_base||bul.brut_total||0);
  const transport = parseFloat(bul.transport||0);
  const sursalaire = parseFloat(bul.sursalaire||0);
  const indRep = parseFloat(bul.indemnite_representation||0);
  const loge = parseFloat(bul.logement||0);
  const ancPct = parseFloat(bul.taux_anciennete_pct||0)/100;
  const ancMontant = Math.round(brut * ancPct);
  const joursTravailles = parseFloat(bul.jours_travailles) || 30;
  const nbEnfants = parseInt(bul.nb_enfants) || 0;
  // §3.25 — CMU : personnes couvertes = employé(e) + conjoint(e) (si
  // marié(e)) + enfants à charge, sur demande explicite des RH.
  const estMarie = /mari/i.test(bul.situation_matrimoniale || '');
  const cmuPersonnes = nbEnfants + 1 + (estMarie ? 1 : 0);

  // Gains (indemnité de transport exclue ici : affichée uniquement dans la
  // section "exonérée" après les retenues, pour éviter la duplication)
  const gains = [];
  if (brut > 0) gains.push({ libelle: 'Salaire catégoriel', base: brut, taux: 0, montant: brut, type_ligne:'gain' });
  if (sursalaire > 0) gains.push({ libelle: 'Sursalaire', base: sursalaire, taux: 0, montant: sursalaire, type_ligne:'gain' });
  if (ancMontant > 0) gains.push({ libelle: `Prime d'ancienneté (${(bul.taux_anciennete_pct||0)}%)`, base: brut, taux: ancPct, montant: ancMontant, type_ligne:'gain' });
  if (indRep > 0) gains.push({ libelle: 'Indemnité de représentation', base: 0, taux: 0, montant: indRep, type_ligne:'gain' });
  if (loge > 0) gains.push({ libelle: 'Indemnité de logement', base: 0, taux: 0, montant: loge, type_ligne:'gain' });

  // Brut imposable : salaire + sursalaire + ancienneté uniquement (transport/
  // logement/représentation NON imposables), proratisé sur jours_travailles/30
  const baseImposable = Math.round((brut + sursalaire + ancMontant) * joursTravailles / 30);

  // Retenues salariales
  const its = parseFloat(bul.its||0);
  const cnpsSal = parseFloat(bul.cnps_sal) || Math.round(baseImposable * PAIE_PARAMS.tauxCnpsRetraiteSalarie);
  const cmuSal  = bul.cmu_sal !== undefined && bul.cmu_sal !== null ? parseFloat(bul.cmu_sal) : (PAIE_PARAMS.cmuMontantSalarial * cmuPersonnes);
  const retenues = [];
  retenues.push({ libelle: `CNPS – Retraite (salarié ${(PAIE_PARAMS.tauxCnpsRetraiteSalarie*100).toFixed(1)}%)`, base: baseImposable, taux: PAIE_PARAMS.tauxCnpsRetraiteSalarie, montant: cnpsSal, type_ligne:'retenue' });
  if (cmuSal > 0) retenues.push({ libelle: `CMU – Couverture Maladie (salariale, ${PAIE_PARAMS.cmuMontantSalarial} × ${cmuPersonnes} pers.)`, base: 0, taux: 0, montant: cmuSal, type_ligne:'retenue' });
  // Fallback pour bulletins legacy sans lignes_bulletin détaillées : ITS déjà
  // net (ICF − RICF, cf PaieController::bulletinCreate). Le détail ICF/RICF
  // n'est disponible que via les lignes_bulletin (cas normal, cf hasLignes).
  if (its > 0) retenues.push({ libelle: 'ITS — Impôt sur les Traitements et Salaires (ICF − RICF)', base: baseImposable, taux: 0, montant: its, type_ligne:'retenue' });

  // Charges patronales
  const plafondPfAt = PAIE_PARAMS.plafondCnpsPfAt;
  const cmuPat = PAIE_PARAMS.cmuMontantPatronal * cmuPersonnes;
  const charges = [];
  charges.push({ libelle: `CNPS – Retraite patronal (${(PAIE_PARAMS.tauxCnpsRetraitePatronal*100).toFixed(1)}%)`, base: Math.min(baseImposable, PAIE_PARAMS.plafondCnpsRetraite), taux_pat: PAIE_PARAMS.tauxCnpsRetraitePatronal, montant: Math.round(Math.min(baseImposable,PAIE_PARAMS.plafondCnpsRetraite)*PAIE_PARAMS.tauxCnpsRetraitePatronal), montant_pat: Math.round(Math.min(baseImposable,PAIE_PARAMS.plafondCnpsRetraite)*PAIE_PARAMS.tauxCnpsRetraitePatronal), type_ligne:'charge_pat' });
  charges.push({ libelle: `CNPS – Prestations Familiales (${(PAIE_PARAMS.tauxCnpsPrestationsFamiliales*100).toFixed(2)}%)`, base: Math.min(baseImposable, plafondPfAt), taux_pat: PAIE_PARAMS.tauxCnpsPrestationsFamiliales, montant: Math.round(Math.min(baseImposable,plafondPfAt)*PAIE_PARAMS.tauxCnpsPrestationsFamiliales), montant_pat: Math.round(Math.min(baseImposable,plafondPfAt)*PAIE_PARAMS.tauxCnpsPrestationsFamiliales), type_ligne:'charge_pat' });
  charges.push({ libelle: `CNPS – Accidents du Travail (${(PAIE_PARAMS.tauxCnpsAccidentTravail*100).toFixed(0)}%)`, base: Math.min(baseImposable, plafondPfAt), taux_pat: PAIE_PARAMS.tauxCnpsAccidentTravail, montant: Math.round(Math.min(baseImposable,plafondPfAt)*PAIE_PARAMS.tauxCnpsAccidentTravail), montant_pat: Math.round(Math.min(baseImposable,plafondPfAt)*PAIE_PARAMS.tauxCnpsAccidentTravail), type_ligne:'charge_pat' });
  charges.push({ libelle: `FDFP – Taxe d'Apprentissage (${(PAIE_PARAMS.tauxFdfpApprentissage*100).toFixed(1)}%)`, base: baseImposable, taux_pat: PAIE_PARAMS.tauxFdfpApprentissage, montant: Math.round(baseImposable*PAIE_PARAMS.tauxFdfpApprentissage), montant_pat: Math.round(baseImposable*PAIE_PARAMS.tauxFdfpApprentissage), type_ligne:'charge_pat' });
  charges.push({ libelle: `FDFP – Formation Professionnelle Continue (${(PAIE_PARAMS.tauxFdfpFormation*100).toFixed(1)}%)`, base: baseImposable, taux_pat: PAIE_PARAMS.tauxFdfpFormation, montant: Math.round(baseImposable*PAIE_PARAMS.tauxFdfpFormation), montant_pat: Math.round(baseImposable*PAIE_PARAMS.tauxFdfpFormation), type_ligne:'charge_pat' });
  if (cmuPat > 0) charges.push({ libelle: `CMU – Couverture Maladie (patronale, ${PAIE_PARAMS.cmuMontantPatronal} × ${cmuPersonnes} pers.)`, base: 0, taux_pat: 0, montant: cmuPat, montant_pat: cmuPat, type_ligne:'charge_pat' });
  charges.push({ libelle: `IS – Impôt sur les Salaires (${(PAIE_PARAMS.tauxImpotSalaires*100).toFixed(1)}%)`, base: baseImposable, taux_pat: PAIE_PARAMS.tauxImpotSalaires, montant: Math.round(baseImposable*PAIE_PARAMS.tauxImpotSalaires), montant_pat: Math.round(baseImposable*PAIE_PARAMS.tauxImpotSalaires), type_ligne:'charge_pat' });

  return { gains, retenues, charges };
}

/** Point d'entrée UNIQUE pour extraire {gains, retenues, charges} d'un
 *  bulletin, utilisé à la fois par l'ancien et le nouveau format d'aperçu —
 *  garantit que les deux formats affichent TOUJOURS les mêmes montants
 *  (même source de données), seule la mise en page diffère. */
function extractLignes(bul) {
  const hasLignes = bul.lignes && bul.lignes.length > 0;
  return hasLignes
    ? {
        gains:    bul.lignes.filter(l => l.type_ligne === 'gain'),
        retenues: bul.lignes.filter(l => l.type_ligne === 'retenue'),
        charges:  bul.lignes.filter(l => l.type_ligne === 'charge_pat'),
      }
    : calcLignesBulletin(bul);
}

/* §3.51 — L'aperçu « ancien format » a été supprimé : seul ApercuBulletinV2
 *  (modèle BULLETIN REVISION.xlsx) est désormais utilisé. */


// ═══════════════════════════════════════════════════════════════════════════════
// APERÇU BULLETIN — NOUVEAU FORMAT (reproduction fidèle de BULLETIN REVISION.xlsx)
// ═══════════════════════════════════════════════════════════════════════════════
/** Reproduit la mise en page exacte du fichier BULLETIN REVISION.xlsx fourni
 *  par les RH, y compris la répartition du coût par projet en colonnes
 *  (TOTAL / PART GFM3 / PART PROJET 1/2/3). Les montants proviennent de la
 *  MÊME source que l'ancien format (extractLignes(bul)) — les deux formats
 *  affichent donc toujours des chiffres identiques, seule la présentation
 *  change. La répartition par projet (nom + %) est éditable sur cet écran ;
 *  GFM3 récupère automatiquement le pourcentage restant, comme dans le
 *  fichier Excel (cellule D29 = C29-E29-F29-G29). Un bouton "Enregistrer la
 *  répartition" permet de la persister en base (colonne
 *  bulletins_paie.repartition_projet, §3.12) via un cycle
 *  suppression/recréation du bulletin (même mécanisme que EditBulletinModal),
 *  disponible uniquement pour un bulletin déjà enregistré (bul.id présent,
 *  pas un aperçu de pré-génération). */
function ApercuBulletinV2({ bul, onBack, showToast, onSaved }) {
  const { gains, retenues, charges } = extractLignes(bul);
  const previewRef = useRef(null);
  const [generating, setGenerating] = React.useState(false);
  const [savingRepart, setSavingRepart] = React.useState(false);

  const findL = (arr, kws) => arr.find(l => kws.every(k => (l.libelle || '').toLowerCase().includes(k.toLowerCase())));
  const val   = l => l ? Math.abs(parseFloat(l.montant) || 0) : 0;

  // §3.27 (révisé §3.28) — Répartition par projet — jusqu'à 5 projets,
  // pré-remplie depuis bul.repartition_projets (PLURIEL — c'est le nom
  // réellement renvoyé par bulletinShow(), la table
  // bulletin_repartition_projet, avec les colonnes nom_projet/pourcentage)
  // si une répartition a déjà été SAISIE MANUELLEMENT et enregistrée en
  // base (§3.12/§3.26). Avant §3.27, le code lisait
  // `bul.repartition_projet` (SINGULIER, un champ qui n'existe jamais dans
  // la réponse API) : le panneau réaffichait donc systématiquement une
  // liste vide au rechargement, même quand la répartition avait bel et
  // bien été enregistrée côté serveur.
  // §3.28 — On ne pré-remplit QUE les lignes manuelles (projet_id = NULL
  // en base, cf. bulletinCreate §3.26), en excluant les lignes calculées
  // AUTOMATIQUEMENT (feuilles de temps ou affectation employe_projet_
  // bailleur, projet_id non NULL) : ces dernières peuvent légitimement
  // désigner un projet "institutionnel" GFM3 à 100 %, ce qui créait une
  // colonne "PART GFM3 INSTITUTIONNEL" dupliquant la colonne "PART GFM3"
  // (déjà calculée séparément comme le complément à 100 %) quand la
  // répartition automatique était chargée par erreur dans ce panneau.
  const [projets, setProjets] = useState(() => (
    Array.isArray(bul.repartition_projets)
      ? bul.repartition_projets.map(rp => ({
            nom: rp.nom_projet || rp.nom || '',
            pct: parseFloat(rp.pourcentage ?? rp.pct) || 0,
          }))
      : []
  ));
  const addProjet    = () => projets.length < 5 && setProjets([...projets, { nom: `Projet ${projets.length + 1}`, pct: 0 }]);
  const updateProjet = (i, k, v) => setProjets(p => p.map((pr, idx) => idx === i ? { ...pr, [k]: v } : pr));
  const removeProjet = i => setProjets(p => p.filter((_, idx) => idx !== i));

  const sumPct  = projets.reduce((s, p) => s + (parseFloat(p.pct) || 0), 0) / 100;
  const pctGfm3 = Math.max(0, 1 - sumPct);
  // §3.18 — Le pourcentage de chaque colonne est affiché directement dans
  // l'en-tête (ex: "PART GFM3 (80,0%)"), sur demande explicite des RH, pour
  // ne plus avoir à se référer au panneau de configuration pour connaître
  // la clé de répartition de chaque colonne.
  const fmtPct = p => `${(p * 100).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  const columns = [
    { key: 'total', label: 'TOTAL',     pct: 1 },
    { key: 'gfm3',  label: `PART GFM3 (${fmtPct(pctGfm3)})`, pct: pctGfm3 },
    ...projets.map((p, i) => {
      const pct = (parseFloat(p.pct) || 0) / 100;
      return { key: `p${i}`, label: `PART ${(p.nom || 'PROJET ' + (i + 1)).toUpperCase()} (${fmtPct(pct)})`, pct };
    }),
  ];

  const handleSaveRepartition = async () => {
    if (!bul.id || bul._preview) {
      showToast && showToast("Générez d'abord le bulletin avant d'enregistrer la répartition", 'error');
      return;
    }
    setSavingRepart(true);
    try {
      await deleteBulletin(bul.id);
      await createBulletin({
        ...bul,
        employe_id:                bul.employe_id,
        anciennete_annees:          parseFloat(bul.anciennete) || 0,
        indemnite_representation:   parseFloat(bul.indemnite_representation) || 0,
        jours_travailles:           parseFloat(bul.jours_travailles) || 30,
        repartition_projet:         projets,
      });
      showToast && showToast('Répartition enregistrée sur le bulletin');
      onSaved && onSaved();
    } catch (err) {
      showToast && showToast(err.response?.data?.error || "Erreur lors de l'enregistrement de la répartition", 'error');
    }
    setSavingRepart(false);
  };

  // ── Valeurs de base ──
  const base   = parseFloat(bul.salaire_base) || 0;
  const sursal = parseFloat(bul.sursalaire)   || 0;
  const primeAnc = val(findL(gains, ["ancienneté"]));
  const totalBrutImposableNominal = base + sursal + primeAnc;
  const joursTravailles = parseFloat(bul.jours_travailles) || 30;
  const brutProrata = parseFloat(bul.brut_total) || 0;

  // ── Charges patronales (détail) ──
  const itsEmployeur    = val(findL(charges, ['impôt', 'salaires']));
  const fdfpApprenti    = val(findL(charges, ['apprentissage']));
  const fdfpFormation   = val(findL(charges, ['formation']));
  const cnpsPatPF       = val(findL(charges, ['prestations familiales']));
  const cnpsPatAT       = val(findL(charges, ['accidents du travail']));
  const cnpsPatRetraite = val(findL(charges, ['retraite patronal']));
  const cmuPat          = val(findL(charges, ['cmu']));

  // ── Charges salariales (détail) ──
  const icf        = val(findL(retenues, ['impôt sur les charges familiales']));
  const ricf       = val(findL(retenues, ['réduction']));
  const itsLegacy   = val(findL(retenues, ['impôt sur le', 'traitement'])); // repli anciens bulletins sans ICF/RICF détaillés
  const cnpsSal     = val(findL(retenues, ['retraite']));
  const cmuSal      = val(findL(retenues, ['cmu']));
  const tropPercu   = parseFloat(bul.trop_percu) || 0;

  const totalChargesFiscalesEmp = itsEmployeur + fdfpApprenti + fdfpFormation;
  const totalChargesSocialesEmp = cnpsPatPF + cnpsPatAT + cnpsPatRetraite + cmuPat;
  const totalChargesPatronales  = totalChargesFiscalesEmp + totalChargesSocialesEmp;

  // §3.23 — ITS (salarial) = ICF − RICF (valeur absolue de la réduction),
  // sur demande explicite des RH. `ricf` est déjà une valeur absolue (via
  // val()), donc Math.abs() ici est une sécurité supplémentaire au cas où
  // le montant stocké changerait de convention de signe.
  const itsCalcule = Math.max(0, icf - Math.abs(ricf));
  const totalChargesFiscalesSal = (icf > 0 || ricf > 0) ? itsCalcule : itsLegacy;
  const totalChargesSocialesSal = cnpsSal + cmuSal;
  const totalChargesSalariales  = totalChargesFiscalesSal + totalChargesSocialesSal;

  // §3.19 — TOTAL NET APRÈS RETENUES = brut imposable prorata − charges
  // SALARIALES uniquement (ligne "TOTAL DES RETENUES" supprimée), sur
  // demande explicite des RH. Remplace la formule précédente (§3.1/§3.14)
  // qui déduisait à la fois les charges patronales ET salariales.
  const totalNetApresRetenues  = brutProrata - totalChargesSalariales;

  const transport = parseFloat(bul.transport) || 0;
  const indemRep  = parseFloat(bul.indemnite_representation) || 0;
  // §3.20 — NET À PAYER = TOTAL NET APRÈS RETENUES + Indemnité de transport
  // + Indemnité de représentation − Trop perçu, TOUJOURS recalculé en
  // direct à partir des valeurs affichées ci-dessus (et non plus depuis
  // bul.net_a_payer, qui restait figé sur l'ancienne formule pour les
  // bulletins déjà enregistrés avant le changement §3.19, causant un
  // écart entre "TOTAL NET APRÈS RETENUES" et "NET À PAYER" affichés).
  const netAPayer = totalNetApresRetenues + transport + indemRep - tropPercu;

  // ── Lignes du tableau de répartition (générique, n colonnes) ──
  // "base" = base de calcul affichée dans la colonne "Base" du fichier
  // Excel (colonne B) : la plupart des charges patronales/salariales sont
  // calculées sur le brut imposable prorata, sauf CNPS PF/AT (plafonnées à
  // 75 000 FCFA) et les lignes sans base (totaux, CMU, RICF...). Valeurs
  // reprises directement du champ `base` déjà présent sur chaque ligne
  // (même source que l'ancien format).
  const rows = [
    { label: 'SALAIRES BRUTS IMPOSABLES', base: brutProrata, total: brutProrata, type: 'normal' },
    { label: 'CHARGES FISCALES EMPLOYEURS', total: totalChargesFiscalesEmp, type: 'section' },
    { label: 'ITS / Impôt sur le Traitement des Salaires (1,2%)', base: findL(charges, ['impôt', 'salaires'])?.base, total: itsEmployeur, type: 'sub' },
    { label: "FDFP / Taxe d'Apprentissage (0,4%)", base: findL(charges, ['apprentissage'])?.base, total: fdfpApprenti, type: 'sub' },
    { label: 'FDFP / Formation Professionnelle Continue (0,6%)', base: findL(charges, ['formation'])?.base, total: fdfpFormation, type: 'sub' },
    { label: 'CHARGES SOCIALES EMPLOYEURS', total: totalChargesSocialesEmp, type: 'section' },
    { label: 'CNPS / Prestations Familiales (5,75%)', base: findL(charges, ['prestations familiales'])?.base, total: cnpsPatPF, type: 'sub' },
    { label: 'CNPS / Accidents du Travail (2%)', base: findL(charges, ['accidents du travail'])?.base, total: cnpsPatAT, type: 'sub' },
    { label: 'CNPS / Caisse de Retraite (7,7%)', base: findL(charges, ['retraite patronal'])?.base, total: cnpsPatRetraite, type: 'sub' },
    { label: 'CMU / Couverture Maladie Universelle (patronale)', total: cmuPat, type: 'sub' },
    { label: 'TOTAL CHARGES PATRONALES', total: totalChargesPatronales, type: 'total' },
    { label: 'CHARGES FISCALES SALARIALES', total: totalChargesFiscalesSal, type: 'section', group: 'salariale' },
    { label: 'Impôt sur les Charges Familiales (ICF)', base: findL(retenues, ['impôt sur les charges familiales'])?.base, total: icf, type: 'sub', group: 'salariale' },
    { label: "Réduction d'Impôt sur Charges Familiales (RICF)", total: -Math.abs(ricf), type: 'sub', group: 'salariale' },
    // §3.23 — cette ligne affichait auparavant `itsLegacy` (0 pour tout
    // bulletin moderne avec ICF/RICF détaillés séparément, faute de ligne
    // "ITS" combinée en base) au lieu du montant réellement dû. Elle
    // reprend maintenant `totalChargesFiscalesSal` (= ICF − |RICF|, avec
    // repli sur itsLegacy pour les tout premiers bulletins sans ICF/RICF).
    { label: 'ITS / Impôt sur le Traitement des Salaires', base: findL(retenues, ['impôt sur les charges familiales'])?.base, total: totalChargesFiscalesSal, type: 'sub', group: 'salariale' },
    { label: 'CHARGES SOCIALES SALARIALES', total: totalChargesSocialesSal, type: 'section', group: 'salariale' },
    { label: 'CNPS / Caisse de Retraite (6,3%)', base: findL(retenues, ['retraite'])?.base, total: cnpsSal, type: 'sub', group: 'salariale' },
    { label: 'CMU / Couverture Maladie Universelle (salariale)', total: cmuSal, type: 'sub', group: 'salariale' },
    { label: 'TOTAL CHARGES SALARIALES', total: totalChargesSalariales, type: 'total', group: 'salariale' },
    { label: 'TOTAL NET APRÈS RETENUES', total: totalNetApresRetenues, type: 'total' },
    { label: 'Indemnités de Transport', base: transport, total: transport, type: 'sub' },
    { label: 'Indemnités de Représentation', total: indemRep, type: 'sub' },
    { label: 'Trop / Moins Perçus', total: -tropPercu, type: 'sub' },
    { label: 'NET À PAYER', total: netAPayer, type: 'net' },
  ].filter(Boolean);

  const handleGeneratePDF = async () => {
    if (!previewRef.current) return;
    setGenerating(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);
      const canvas = await html2canvas(previewRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2;
      // Le bulletin doit toujours tenir sur UNE seule page, quel que soit le
      // nombre de projets/colonnes de répartition ajoutés (demande RH) :
      // on calcule une échelle qui fait rentrer tout le bulletin dans la
      // page, plutôt que de le découper sur plusieurs pages.
      // §3.17 — Priorité à la LARGEUR de page (élimine les marges gauche/
      // droite inutiles à l'impression A4, sur demande explicite des RH) :
      // on ne réduit l'échelle par la hauteur que si le fait de remplir
      // toute la largeur ferait déborder le bulletin sur une 2e page.
      const scaleW = availW / canvas.width;
      const scaleH = availH / canvas.height;
      const scale = (canvas.height * scaleW <= availH) ? scaleW : scaleH;
      const imgW = canvas.width * scale;
      const imgH = canvas.height * scale;
      const x = margin + (availW - imgW) / 2;
      const y = margin;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgW, imgH);
      const nom = `${bul.nom || ''}_${bul.prenoms || ''}`.replace(/\s+/g, '_');
      pdf.save(`Bulletin_${nom}_${MOIS[bul.mois]}_${bul.annee}_V2.pdf`);
    } catch (err) {
      console.error('Erreur PDF:', err);
      alert('Erreur : vérifiez que jspdf et html2canvas sont installés (npm install jspdf html2canvas)');
    }
    setGenerating(false);
  };

  return (
    <>
      <div className="pr-toolbar">
        <button className="pr-btn gray" onClick={onBack}>← Retour</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="pr-btn dark" onClick={handleGeneratePDF} disabled={generating}>
            <IcoPrint /> {generating ? '⏳ Génération…' : '📄 Générer PDF'}
          </button>
        </div>
      </div>

      {/* §3.30 — Panneau retiré : répartition depuis la fiche employé. */}

      <div className="pr-preview" ref={previewRef}>
        {/* Header organisation (identique à BULLETIN REVISION.xlsx) */}
        <div className="pr-preview-hdr">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/logo_gfm3.png" alt="GFM3" style={{ height: 72, width: 'auto', borderRadius: 4 }} />
            <div>
              <div className="pr-preview-org">GFM3 — Génération Femme du Troisième Millénaire</div>
              <div className="pr-preview-sub">
                Adresse : {ORG_INFO.adresse} | Siège social : {ORG_INFO.siege}<br />
                N°CNPS : {ORG_INFO.cnps} | N°Contribuable : {ORG_INFO.contribuable}<br />
                Email : {ORG_INFO.email} | Téléphone : {ORG_INFO.telephone}
              </div>
            </div>
          </div>
          <div>
            <div className="pr-preview-badge">BULLETIN DE SALAIRE</div>
            <div className="pr-preview-period">
              Mois : {MOIS[bul.mois]} {bul.annee}<br />
              Date d'édition : {dateEdition()}
            </div>
          </div>
        </div>

        {/* Infos employé (ordre et libellés identiques au fichier Excel) */}
        <div className="pr-info-grid">
          <div className="pr-info-block">
            <div className="pr-info-block-title">Informations Employé(e)</div>
            {[
              ['Nom',                    bul.nom || '—'],
              ['Prénoms',                bul.prenoms || '—'],
              ['Contact',                bul.contact || bul.telephone || '—'],
              ['N°CNPS',                 bul.num_cnps  || '—'],
              ["Nombre d'enfant",        bul.nb_enfants ?? '—'],
              ['Situation matrimoniale', bul.situation_matrimoniale || 'Célibataire'],
              ['Nombre de part',         bul.nombre_parts || 1],
            ].map(([k, v]) => (
              <div key={k} className="pr-info-row">
                <span className="pr-info-key">{k} :</span>
                <span className="pr-info-val">{v}</span>
              </div>
            ))}
          </div>
          <div className="pr-info-block">
            <div className="pr-info-block-title">Informations Contrat</div>
            {[
              ['Matricule',              bul.matricule || '—'],
              ['Ville',                  bul.bureau_ville || bul.ville || 'Abidjan'],
              ['Emploi occupé',          bul.fonction || '—'],
              ['Type de contrat',        bul.type_contrat || '—'],
              ["Date d'embauche",        fmtDateFR(bul.date_debut)],
              ['Date de fin de contrat', bul.type_contrat === 'CDI' ? 'CDI' : fmtDateFR(bul.date_fin)],
              ['Ancienneté',             fmtAnciennete(calcAnciennete(bul.date_debut, bul.mois, bul.annee))],
              ['Catégorie',              bul.categorie || '—'],
            ].map(([k, v]) => (
              <div key={k} className="pr-info-row">
                <span className="pr-info-key">{k} :</span>
                <span className="pr-info-val">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bande de section — comble l'espace laissé par le bloc "Informations
            Contrat" (plus court que "Informations Employé(e)") et introduit
            clairement la section Rubriques/Valeurs qui suit, comme dans
            l'ancien format où "ÉLÉMENTS DE SALAIRE" sépare les sections. */}
        <div className="pr-section-band">ÉLÉMENTS DE SALAIRE</div>

        {/* Rubriques / Valeurs (2 colonnes, comme le fichier Excel) */}
        <div className="pr-info-grid">
          <div className="pr-info-block">
            <div className="pr-info-row"><span className="pr-info-key">Nombre de jours travaillé :</span><span className="pr-info-val">{joursTravailles}</span></div>
            <div className="pr-info-row"><span className="pr-info-key">Salaire catégoriel :</span><span className="pr-info-val">{fmt(base)}</span></div>
            <div className="pr-info-row"><span className="pr-info-key">Sursalaire :</span><span className="pr-info-val">{fmt(sursal)}</span></div>
          </div>
          <div className="pr-info-block">
            <div className="pr-info-row"><span className="pr-info-key">Prime d'ancienneté :</span><span className="pr-info-val">{fmt(primeAnc)}</span></div>
            <div className="pr-info-row"><span className="pr-info-key">Total brut imposable :</span><span className="pr-info-val">{fmt(totalBrutImposableNominal)}</span></div>
            <div className="pr-info-row"><span className="pr-info-key">Brut imposable prorata :</span><span className="pr-info-val">{fmt(brutProrata)}</span></div>
          </div>
        </div>

        {/* Tableau de répartition (BASES | REPARTITION : TOTAL / PART GFM3 / PART PROJETS) —
            deux lignes d'en-tête comme le fichier Excel (ligne "BASES"/"REPARTITION"
            au-dessus, puis "TOTAL"/"PART GFM3"/"PART PROJET n" en dessous) */}
        <table className="pr-btbl">
          <thead>
            <tr>
              <th rowSpan={2}>Rubriques</th>
              <th rowSpan={2} className="r">Base</th>
              <th colSpan={columns.length} style={{ textAlign: 'center' }}>RÉPARTITION</th>
            </tr>
            <tr>
              {columns.map(c => <th key={c.key} className="r">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              // Le bloc "CHARGES ... SALARIALES" reçoit une teinte violette
              // douce et distincte (group: 'salariale') pour bien le
              // séparer visuellement du bloc patronal (vert), sur demande
              // explicite des RH.
              const isSal = r.group === 'salariale';
              const rowClass = isSal
                ? (r.type === 'section' ? 'sal-sec-row' : r.type === 'total' ? 'sal-tot-row' : r.type === 'sub' ? 'sal-sub-row' : '')
                : (r.type === 'section' ? 'sec-row' : r.type === 'total' ? 'tot-row' : r.type === 'net' ? 'net-row-v2' : r.type === 'sub' ? 'sub-row' : '');
              return (
                <tr key={i} className={rowClass}>
                  <td style={{ fontWeight: r.type === 'normal' ? 400 : r.type === 'sub' ? 400 : 700 }}>{r.label}</td>
                  <td className="r">{r.base ? fmt(r.base) : '—'}</td>
                  {columns.map(c => (
                    <td key={c.key} className="r">{fmt(r.total * c.pct)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* §3.16 — Note "Mode de paiement" supprimée et zone de signatures
            remontée juste sous le tableau, sur demande explicite des RH
            (espace vide inutile en bas du bulletin à l'impression). */}
        <div className="pr-sig-row">
          <div className="pr-sig">Visa de l'employé</div>
          <div className="pr-sig">Visa des Ressources Humaines</div>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// APERÇU FACTURE
// ═══════════════════════════════════════════════════════════════════════════════
function nbrEnLettres(n) {
  const u=['','un','deux','trois','quatre','cinq','six','sept','huit','neuf','dix','onze','douze','treize','quatorze','quinze','seize','dix-sept','dix-huit','dix-neuf'];
  const d=['','','vingt','trente','quarante','cinquante','soixante','soixante','quatre-vingt','quatre-vingt'];
  if(!n||n===0)return'zéro';
  let r='',x=Math.round(n);
  if(x>=1000000){r+=nbrEnLettres(Math.floor(x/1000000))+' million'+(Math.floor(x/1000000)>1?'s':'')+' ';x%=1000000;}
  if(x>=1000){const m=Math.floor(x/1000);r+=(m===1?'mille':nbrEnLettres(m)+' mille')+' ';x%=1000;}
  if(x>=100){const c=Math.floor(x/100);r+=(c===1?'cent':nbrEnLettres(c)+' cent')+(x%100===0&&c>1?'s':x%100===0?'':' ');x%=100;}
  if(x>=20){const di=Math.floor(x/10),un=x%10;if(di===7||di===9){r+=(di===7?'soixante':'quatre-vingt')+'-'+(u[di===7?10+un:10+un]||'dix');}else{r+=d[di]+(un===1&&di!==8?'-et-':un>0?'-':di===8?'s':'')+u[un];}}
  else if(x>0)r+=u[x];
  return r.trim();
}

function ApercuFacture({ fac, onBack }) {
  const previewRef = React.useRef(null);
  const [generating, setGenerating] = React.useState(false);

  const handleGeneratePDF = async () => {
    if (!previewRef.current) return;
    setGenerating(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'), import('html2canvas'),
      ]);
      const canvas = await html2canvas(previewRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth(), pageH = pdf.internal.pageSize.getHeight();
      const margin = 8, imgW = pageW - margin * 2;
      const totalImgH = (canvas.height * imgW) / canvas.width, pageImgH = pageH - margin * 2;
      let renderedH = 0, page = 0;
      while (renderedH < totalImgH) {
        if (page > 0) pdf.addPage();
        const drawH = Math.min(pageImgH, totalImgH - renderedH);
        const srcY = (renderedH / totalImgH) * canvas.height;
        const srcH = (drawH / totalImgH) * canvas.height;
        const sc = document.createElement('canvas'); sc.width = canvas.width; sc.height = Math.round(srcH);
        sc.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        pdf.addImage(sc.toDataURL('image/png'), 'PNG', margin, margin, imgW, drawH);
        renderedH += drawH; page++;
      }
      const nom = `${fac.nom||''}_${fac.prenoms||''}`.replace(/\s+/g,'_');
      pdf.save(`Facture_${nom}_${MOIS[fac.mois]}_${fac.annee}.pdf`);
    } catch(err) { console.error(err); alert('Erreur PDF'); }
    setGenerating(false);
  };

  // §3.35 — Consultant → Consultance (BNC 7,5%) ; Prestataire/Bénévole → Prestation (PPSI 2%)
  const type = (fac.type_contrat || '').toLowerCase();
  const estPrestation = type.includes('prestation') || type.includes('bénévol') || type.includes('benevol');
  const rate = estPrestation ? 0.02 : 0.075;
  const titre         = estPrestation ? 'FACTURE DE PRESTATION' : 'FACTURE DE CONSULTANCE';
  const roleLabel     = estPrestation ? 'LE PRESTATAIRE' : 'LE CONSULTANT';
  const honoraireLabel= estPrestation ? 'HONORAIRE DE PRESTATION' : 'HONORAIRE DE CONSULTANCE';
  const brutLabel     = estPrestation ? 'Honoraire Brut de Prestation' : 'Honoraire Brut de Consultance';
  const retLabel      = estPrestation ? 'Paiement des Prestataires du Service Informel PPSI (2%)' : 'Bénéfice Non Commercial BNC (7,5%)';
  const signLabel     = estPrestation ? 'SIGNATURE DU PRESTATAIRE' : 'SIGNATURE DU CONSULTANT';

  const brut = parseFloat(fac.montant_brut) || 0;
  const ret  = Math.round(brut * rate);
  const net  = brut - ret;

  const dateEdit = new Date().toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric' });
  const fmtD = d => { if(!d) return '—'; const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : d; };
  const dateDebut = fmtD(fac.date_debut);
  const dateFin   = fac.type_contrat === 'CDI' ? 'CDI' : fmtD(fac.date_fin);
  const lastDay   = fac.mois && fac.annee ? new Date(fac.annee, fac.mois, 0).getDate() : 30;
  const periodeDu = fac.mois ? `01/${String(fac.mois).padStart(2,'0')}/${fac.annee}` : '—';
  const periodeAu = fac.mois ? `${lastDay}/${String(fac.mois).padStart(2,'0')}/${fac.annee}` : '—';

  const netLettres = nbrEnLettres(Math.round(net));
  const netStr = netLettres.charAt(0).toUpperCase() + netLettres.slice(1) + ' francs CFA';

  const vent = (Array.isArray(fac.repartition_projets) && fac.repartition_projets.length > 0)
    ? fac.repartition_projets.map(rp => {
        const pct = parseFloat(rp.pourcentage) || 0;
        const mb = Math.round(brut * pct / 100);
        const rf = Math.round(mb * rate);
        return { nom: rp.nom_projet || '—', bailleur: rp.bailleur || '—', budget: rp.budget_code || '—',
                 subvention: rp.subvention || '—', numero: rp.numero_projet || '—', fond: rp.code_fond || '—',
                 pct, mb, rf, net: mb - rf };
      })
    : [{ nom: fac.projet_nom || 'GFM3', bailleur: fac.partenaire || 'GFM3', budget: fac.budget_code || '—',
         subvention: fac.subvention || '—', numero: '—', fond: '—', pct: 100, mb: brut, rf: ret, net }];

  const thS = { background:'#0f2644', color:'#fff', padding:'5px 6px', fontSize:9.5, fontWeight:700, border:'1px solid #0f2644', textAlign:'center' };
  const tdS = { border:'1px solid #cbd5e1', padding:'4px 6px', fontSize:10, verticalAlign:'middle' };

  return (
    <>
      <div className="pr-toolbar">
        <button className="pr-btn gray" onClick={onBack}>← Retour</button>
        <button className="pr-btn dark" onClick={handleGeneratePDF} disabled={generating}>
          <IcoPrint /> {generating ? '⏳ Génération…' : '📄 Générer PDF'}
        </button>
      </div>

      <div className="pr-preview" ref={previewRef} style={{fontFamily:"'DM Sans',Arial,sans-serif",fontSize:12,color:'#111',background:'#fff'}}>
        <div style={{textAlign:'center',fontWeight:800,fontSize:16,letterSpacing:1,marginBottom:12,color:'#0f2644'}}>{titre}</div>

        <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap'}}>
          <div style={{display:'flex',border:'1.5px solid #0f2644',borderRadius:4,overflow:'hidden',fontSize:11}}>
            <span style={{background:'#0f2644',color:'#fff',padding:'4px 10px',fontWeight:700}}>MATRICULE</span>
            <span style={{padding:'4px 14px',fontWeight:600}}>{fac.matricule||'—'}</span>
          </div>
          <div style={{display:'flex',border:'1.5px solid #0f2644',borderRadius:4,overflow:'hidden',fontSize:11}}>
            <span style={{background:'#0f2644',color:'#fff',padding:'4px 10px',fontWeight:700}}>N° FACTURE</span>
            <span style={{padding:'4px 14px',fontWeight:600}}>{fac.num_facture||'—'}</span>
          </div>
          <div style={{marginLeft:'auto',fontSize:11,fontWeight:600}}>DATE : {dateEdit}</div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <div style={{border:'1px solid #cbd5e1',borderRadius:6,overflow:'hidden'}}>
            <div style={{background:'#0f2644',color:'#fff',padding:'5px 10px',fontWeight:700,fontSize:12}}>{roleLabel}</div>
            {[
              ['Nom et Prénoms', `${fac.nom||''} ${fac.prenoms||''}`, true],
              ['Contact', fac.telephone||fac.contact||'—', false],
              ['Fonction', fac.fonction||'—', false],
              ['Début de contrat', dateDebut, false],
              ['Fin de contrat', dateFin, false],
              ['Ancienneté (an(s) mois)', fmtAnciennete(calcAnciennete(fac.date_debut, fac.mois, fac.annee)), false],
              ['Email', fac.email||'—', false],
              ['Site', fac.ville||fac.site||'—', false],
              ['Règlement mois', `${MOIS[fac.mois]||''} ${fac.annee||''}`, false],
              ['__periode__', null, false],
            ].map(([k,v,bold],i) => k==='__periode__' ? (
              <div key={i} style={{display:'flex',padding:'4px 10px',fontSize:11,alignItems:'center',gap:4}}>
                <span style={{minWidth:110,color:'#6b7a93'}}>Période du :</span>
                <span style={{display:'flex',gap:6,alignItems:'center'}}>
                  <span style={{border:'1px solid #cbd5e1',padding:'1px 8px',borderRadius:3}}>{periodeDu}</span>
                  <span style={{color:'#6b7a93'}}>Au</span>
                  <span style={{border:'1px solid #cbd5e1',padding:'1px 8px',borderRadius:3}}>{periodeAu}</span>
                </span>
              </div>
            ) : (
              <div key={i} style={{display:'flex',padding:'4px 10px',borderBottom:'1px solid #f1f5f9',fontSize:11}}>
                <span style={{minWidth:110,color:'#6b7a93'}}>{k} :</span>
                <span style={{fontWeight:bold?700:400}}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{border:'1px solid #cbd5e1',borderRadius:6,padding:'14px 16px',textAlign:'right',display:'flex',flexDirection:'column',justifyContent:'center'}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>DOIT ONG GFM3</div>
            <div style={{fontWeight:700,fontSize:12,lineHeight:1.5,marginBottom:8}}>GENERATION FEMME DU<br/>TROISIEME MILLENAIRE</div>
            <div style={{fontSize:11,lineHeight:1.9,color:'#374151'}}>
              27 BP 283 ABJ 27<br/>27 22 52 20 65<br/>
              <span style={{color:'#1565C0',fontWeight:600}}>gfem3@yahoo.fr</span><br/>
              <span style={{color:'#1565C0',fontWeight:600}}>WWW.GFM3.ORG</span>
            </div>
          </div>
        </div>

        <div style={{border:'1px solid #cbd5e1',borderRadius:6,overflow:'hidden',marginBottom:12}}>
          <div style={{background:'#0f2644',color:'#fff',padding:'5px 10px',fontWeight:700,fontSize:12,textAlign:'center'}}>{honoraireLabel}</div>
          <div style={{display:'flex',justifyContent:'space-between',padding:'5px 14px',borderBottom:'1px solid #f1f5f9',fontSize:11}}>
            <span style={{fontWeight:600}}>{brutLabel} :</span><span>{fmt(brut)} FCFA</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',padding:'5px 14px',borderBottom:'1px solid #f1f5f9',fontSize:11}}>
            <span style={{fontWeight:600}}>{retLabel} :</span><span style={{color:'#991b1b'}}>{fmt(ret)} FCFA</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',padding:'6px 14px',fontSize:12,background:'#f0fdf4'}}>
            <span style={{fontWeight:800}}>NET A PAYER :</span><span style={{fontWeight:800,color:'#166534'}}>{fmt(net)} FCFA</span>
          </div>
        </div>

        <div style={{background:'#0f2644',color:'#fff',padding:'5px 10px',fontWeight:700,fontSize:11,textAlign:'center',borderRadius:'6px 6px 0 0'}}>DÉTAILS DES PRESTATIONS ET VENTILATION PAR PROJET</div>
        <table style={{width:'100%',borderCollapse:'collapse',marginBottom:12}}>
          <thead>
            <tr>
              <th style={thS}>Détails du projet</th>
              <th style={thS}>Code<br/>budgétaire</th>
              <th style={thS}>Numéro<br/>projet</th>
              <th style={thS}>Code du<br/>fond</th>
              <th style={thS}>%</th>
              <th style={thS}>Montant brut de<br/>contribution</th>
              <th style={thS}>Retenues<br/>fiscales ({estPrestation?'2%':'7,5%'})</th>
              <th style={thS}>Net à<br/>payer</th>
            </tr>
          </thead>
          <tbody>
            {vent.map((v,i)=>(
              <tr key={i}>
                <td style={tdS}>
                  <div><span style={{color:'#6b7a93'}}>Organisation partenaire :</span> <strong>{v.bailleur}</strong></div>
                  <div><span style={{color:'#6b7a93'}}>N° de la sous-subvention :</span> {v.subvention}</div>
                  <div><span style={{color:'#6b7a93'}}>Titre du projet :</span> {v.nom}</div>
                </td>
                <td style={{...tdS,textAlign:'center'}}>{v.budget}</td>
                <td style={{...tdS,textAlign:'center'}}>{v.numero}</td>
                <td style={{...tdS,textAlign:'center'}}>{v.fond}</td>
                <td style={{...tdS,textAlign:'center'}}>{v.pct}%</td>
                <td style={{...tdS,textAlign:'right'}}>{fmt(v.mb)}</td>
                <td style={{...tdS,textAlign:'right',color:'#991b1b'}}>{fmt(v.rf)}</td>
                <td style={{...tdS,textAlign:'right',fontWeight:700,color:'#166534'}}>{fmt(v.net)}</td>
              </tr>
            ))}
            <tr>
              <td style={{...tdS,fontWeight:800}} colSpan={4}>TOTAL</td>
              <td style={{...tdS,textAlign:'center',fontWeight:800}}>100%</td>
              <td style={{...tdS,textAlign:'right',fontWeight:800}}>{fmt(brut)}</td>
              <td style={{...tdS,textAlign:'right',fontWeight:800,color:'#991b1b'}}>{fmt(ret)}</td>
              <td style={{...tdS,textAlign:'right',fontWeight:800,color:'#166534'}}>{fmt(net)}</td>
            </tr>
          </tbody>
        </table>

        <div style={{border:'1px solid #cbd5e1',borderRadius:6,padding:'8px 14px',marginBottom:20,fontSize:11,lineHeight:1.8}}>
          <span style={{fontWeight:700}}>Arrêté la présente facture à la somme de : </span>
          <span style={{fontStyle:'italic'}}>{netStr}</span>
        </div>

        <div style={{display:'flex',justifyContent:'flex-end',marginTop:20}}>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:11,fontWeight:700,marginBottom:34}}>{signLabel}</div>
            <div style={{borderBottom:'1px solid #374151',width:220}}/>
          </div>
        </div>
      </div>
    </>
  );
}
